'use client';

import * as ENGINE from '@gnsx/genesys.js';
import { PROJECT_PATH_PREFIX } from '@gnsx/genesys.js';
import CameraControlsImpl from 'camera-controls';
import { compact } from 'es-toolkit';
import { get, invoke } from 'es-toolkit/compat';
import { autorun, reaction, runInAction } from 'mobx';
import { addDisposer, detach, flow, isAlive, t } from 'mobx-state-tree';
import { toast } from 'sonner';
import * as THREE from 'three';

import { getSceneUndoRedo } from '../core/undoRedo';
import { UniversalAccessor } from '../core/universalAccessor';

import {
  CAMERA_STATE_DEFAULT_FLY_SPEED,
  clearPersistedCameraState,
  loadPersistedCameraState,
  savePersistedCameraState
} from './cameraPersistence';
import { editorAssets, editorStore } from './editorStore';
import { EditableObject } from './models/editableObject';
import { SceneTree, SceneTreeNode } from './models/sceneTree';

import type { IClassInfo } from './models/classInfo';
import type { IEditableObject } from './models/editableObject';
import type { IObjectTree, IObjectTreeNode } from './models/objectTree';
import type { ISceneTreeNode } from './models/sceneTree';
import type { CameraName, ViewMode } from './models/types';
import type { EditorObjectData, EditorWorldData, SceneTreeData } from '@/app/editor/core/editorClassData';
import type { ObjectIconBillboard } from '@/editor/core/objectHelpers';
import type { TransformControlsRef } from '@/lib/three/TransformControls';
import type { Instance } from 'mobx-state-tree';

import { DEBUG_SKIP_LOAD_EDITOR_WORLD, DEFAULT_RENDERER_TYPE, PRODUCT_NAME } from '@/const';
import { requestViewportRender, ViewportRenderReason } from '@/editor/components/viewport/canvas/viewportRenderScheduler';
import { actorParenting, nameHelpers, objectActions, objectHelpers, prefabActions } from '@/editor/core';
import { exportActor, loadActorAsync } from '@/editor/core/actions/objectActions';
import { EditorGameLoop, type EditorWorldActorHooks } from '@/editor/core/EditorGameLoop';
import { reportEditorHookError } from '@/editor/core/editorHookErrorReporter';
import { calculateArrayDiff, findBestPlacementPositionByNormalizedScreenPosition } from '@/editor/core/editorUtils';
import { actorLogRef, object3DLogRef } from '@/editor/core/engineLogRefs';
import { syncPlayModeStartingGate } from '@/editor/core/project-file-watch/coordination';
import { SceneBackgroundColors } from '@/editor/core/sceneBackgroundColors';
import { IS_DEV } from '@/lib/utils';

const DEBUG = {
  logSelectedObjects: false,
  logSelectionChanges: false,
  /** Avoid logging live engine/Three objects — client log forwarding stringifies args and can throw in Three's toJSON. */
  logDuplicateObjects: false,
  logBuildOutliner: false,
  logSetRenderer: true,
  logSetCamera: true,
  logSetCameraControls: false,
  logSaveCameraState: IS_DEV,
  logLoadCameraState: IS_DEV,
  logCaptureScreenshot: IS_DEV && false,
} as const;

export { SceneBackgroundColors };

const DEFERRED_INITIAL_SCREENSHOT_MS = 800;

let deferredInitialScreenshotTimer: ReturnType<typeof setTimeout> | null = null;

export function cancelDeferredInitialScreenshot(): void {
  if (deferredInitialScreenshotTimer != null) {
    clearTimeout(deferredInitialScreenshotTimer);
    deferredInitialScreenshotTimer = null;
  }
}

/**
 * After a world load, capture one screenshot if this project has no dashboard thumbnail in IndexedDB yet.
 * Cancelled on world teardown / scene switch.
 */
export function scheduleDeferredInitialScreenshotIfMissing(
  delayMs: number = DEFERRED_INITIAL_SCREENSHOT_MS,
): void {
  if (typeof window === 'undefined') {
    return;
  }
  cancelDeferredInitialScreenshot();
  const world = editorStore.scene.world;
  if (!world) {
    return;
  }
  const worldUuid = world.uuid;

  deferredInitialScreenshotTimer = setTimeout(() => {
    deferredInitialScreenshotTimer = null;
    void (async () => {
      if (editorStore.scene.world?.uuid !== worldUuid) {
        return;
      }
      if (editorStore.scene.isPrefabMode) {
        return;
      }
      if (DEBUG_SKIP_LOAD_EDITOR_WORLD) {
        return;
      }
      const projectFolder = editorStore.project.projectFolder;
      const projectName = editorStore.project.projectName;
      if (!projectFolder || !projectName) {
        return;
      }
      const projectKey = getProjectScreenshotIdbKey(projectFolder);
      if (!projectKey) {
        return;
      }
      let db: IDBDatabase | null = null;
      try {
        db = await openIndexedDB();
        const existing = await loadFromIndexedDB(db, projectKey);
        if (existing) {
          return;
        }
      } catch {
        return;
      } finally {
        db?.close();
      }
      editorStore.scene.captureScreenshot();
    })();
  }, delayMs);
}

const ACTOR_CLIPBOARD_PAYLOAD_TYPE = 'genesys-editor-actors';
const ACTOR_CLIPBOARD_PAYLOAD_VERSION = 1;

interface ActorClipboardPayload {
  type: typeof ACTOR_CLIPBOARD_PAYLOAD_TYPE;
  version: typeof ACTOR_CLIPBOARD_PAYLOAD_VERSION;
  copiedAt: string;
  actors: ENGINE.SerializedData[];
}

export const SceneStore = t
  .model('SceneStore', {
    outliner: t.optional(SceneTree, () => SceneTree.create({ nodes: [] })),
    selectedObjects: t.array(EditableObject),
    canvasResetVersion: t.optional(t.number, 0),
    isTranformingObjects: t.optional(t.boolean, false),
    isCreating: t.optional(t.boolean, false),
    isResettingWorld: t.optional(t.boolean, false),
    isPlayMode: t.optional(t.boolean, false),
    inspectorUpdateCounter: t.optional(t.number, 0),
    missingAssetDiagnosticsEpoch: t.optional(t.number, 0),
  })
  .volatile(() => ({
    isPlayModeStarting: false,
    camera: null as THREE.Camera | null,
    cameraControls: null as THREE.EventDispatcher | null,
    world: null as ENGINE.World | null,
    scene: null as THREE.Scene | null,
    gameLoop: null as EditorGameLoop | null,
    renderer: null as ENGINE.IGenesysRenderer | null,
    rendererType: DEFAULT_RENDERER_TYPE as ENGINE.RendererType,
    /** True once the renderer type has been resolved from project settings (prevents double Canvas mount). */
    rendererTypeResolved: false,
    selectionGroup: new THREE.Group(),
    hiddenGroup: new THREE.Group(),
    transformControls: null as TransformControlsRef | null,
    billboards: new Map<string, ObjectIconBillboard>(),
    cachedSelectedActors: [] as ENGINE.Actor[],
    prefabReturnActorId: null as string | null,
    /** Asset path of the prefab open in prefab editor mode, or null when editing the level scene. */
    prefabEditPath: null as string | null,
    /** In-memory unsaved edits while in prefab editor (explicit save clears this). */
    prefabEditDirty: false,
    backgroundColor: SceneBackgroundColors.default as string,
    /** R3F root scene; viewport draws this scene, not {@link scene} (engine world scene). */
    r3fCanvasScene: null as THREE.Scene | null,
    /**
     * Set to true while a viewport screenshot capture is in progress.
     * EditorLoop skips renderScene for that frame to prevent concurrent
     * WebGPU pipeline calls from corrupting renderer state.
     * Intentionally non-observable (volatile plain JS field) to avoid MobX
     * overhead on the hot render path.
     */
    isCapturingViewportScreenshot: false,
  }))
  .views(self => ({
    get isWebGPU(): boolean {
      return self.rendererType !== 'webgl';
    },
    getSelectedObjectById(id: string) {
      return self.selectedObjects.find(obj => obj.id === id);
    },
    get selectedObject(): IEditableObject | null {
      return self.selectedObjects.length > 0 ? self.selectedObjects[0] : null;
    },
    get selectedActors(): ENGINE.Actor[] {
      return self.selectedObjects.map(obj => obj.asActor).filter((actor): actor is ENGINE.Actor => actor !== undefined) ?? [];
    },
    /** Level world scene (skybox, fog, actors). Prefer {@link scene} for the render root. */
    get levelScene(): THREE.Scene | null {
      return self.world?.scene ?? null;
    },
    get editorOutlineSelectionKey(): string {
      const actorUuids = self.selectedObjects
        .map(obj => obj.asActor?.uuid)
        .filter((actorUuid): actorUuid is string => actorUuid !== undefined)
        .sort();
      return `${self.world?.uuid ?? ''}:${actorUuids.join('|')}`;
    },
    get selectedObjectIds(): string[] {
      return self.selectedObjects.map(obj => obj.id);
    },
    get isPrefabMode(): boolean {
      return self.prefabEditPath != null || (this.selectedObject?.isPrefabAsset ?? false);
    },
    get isEngineAsset(): boolean { return this.selectedObject?.isEngineAsset ?? false; },
    /** Check if viewport selection should be locked (e.g., when instanced geometry tool is active) */
    get isViewportSelectionLocked(): boolean {
      const selectedActorOrComponent = this.selectedObject?.selectedSubobject?.target;
      return selectedActorOrComponent?.getComponent(ENGINE.BaseInstancedMeshComponent) != null;
    },
    getCameraLookAtPosition(distance: number = 10) {
      const camera = self.camera;
      if (!camera) {
        console.warn('No camera found');
        return new THREE.Vector3(0, 0, 0);
      }
      return findBestPlacementPositionByNormalizedScreenPosition({ x: 0, y: 0 }, camera, distance);
    }
  }))
  // Outliner actions
  .actions(self => ({
    updateCachedSelections() {
      // Find what changed in actors
      const oldActors = self.cachedSelectedActors;
      const newActors = self.selectedActors;
      const { added: addedActors, removed: removedActors } = calculateArrayDiff(oldActors, newActors);
      if (DEBUG.logSelectionChanges) {
        console.log('🔄 Selection changes - Actors:', {
          added: addedActors.map(a => a && actorLogRef(a)),
          removed: removedActors.map(a => a && actorLogRef(a)),
        });
      }

      // Update cached values
      self.cachedSelectedActors = newActors;
      addedActors.forEach(actor => {
        if (!actor) return;
        try {
          actor.onEditorSelectedChanged(true, newActors);
        } catch (error) {
          reportEditorHookError({
            hook: 'onEditorSelectedChanged',
            context: 'SceneStore.updateCachedSelections',
            target: actor,
            error,
          });
        }
      });
      removedActors.forEach(actor => {
        if (!actor) return;
        try {
          actor.onEditorSelectedChanged(false, newActors);
        } catch (error) {
          reportEditorHookError({
            hook: 'onEditorSelectedChanged',
            context: 'SceneStore.updateCachedSelections',
            target: actor,
            error,
          });
        }
      });
    },
    updateSelectionGroup() {
      const selectionGroup = self.selectionGroup;
      const world = self.world;
      if (world && selectionGroup) {
        actorParenting.validateAndRepairAttachments(world);
        objectHelpers.detachGroupChildren(selectionGroup, world.scene);
        if (self.selectedActors.length > 1) {
          objectHelpers.attachToGroup(selectionGroup, self.selectedActors);
        }
      }
      this.updateCachedSelections();
    }
  }))
  // Tree management and utility actions
  .actions(self => ({
    setPrefabReturnActorId(actorId: string | null | undefined) {
      self.prefabReturnActorId = getValidatedSceneActorId(self.world, actorId);
    },
    setPrefabEditPath(prefabPath: string | null) {
      self.prefabEditPath = prefabPath && prefabPath.length > 0 ? prefabPath : null;
      if (self.prefabEditPath) {
        self.prefabEditDirty = false;
      }
    },
    setPrefabEditDirty(dirty: boolean) {
      self.prefabEditDirty = dirty;
    },
    resolvePrefabReturnActorId(preferredActorId?: string | null): string | null {
      const preferredId = getValidatedSceneActorId(self.world, preferredActorId);
      if (preferredId) {
        self.prefabReturnActorId = preferredId;
        return preferredId;
      }

      const fallbackId = getValidatedSceneActorId(self.world, self.prefabReturnActorId);
      self.prefabReturnActorId = fallbackId;
      return fallbackId;
    },
  }))
  .actions(self => ({
    /** Get all unique actors from world scene */
    getSceneActors(): ENGINE.Actor[] {
      return self.world?.getActors().filter(actor => !actor.isTransient()) ?? [];
    },
    getSceneActorIds(): Set<string> {
      return new Set(this.getSceneActors().map(actor => actor.uuid));
    },
    initOutliner() {
      console.log('[SceneStore] 🌳 initOutlinerTree...');
      nameHelpers.resetActorNameCounters();
      this.loadOutliner();
    },
    /** Add missing actors to the tree root */
    addActorsToOutliner(sceneActors: ENGINE.Actor[]) {
      const expandedIds = new Set<string>();
      const rootId = self.outliner.rootNode?.id;
      for (const actor of sceneActors) {
        const actorNode = self.outliner.addNode(
          SceneTreeNode.create({
            id: actor.uuid,
            name: nameHelpers.getActorDisplayName(actor),
            isExpanded: undefined,
            nodeType: 'actor',
          }), rootId);
        actorNode?.init(actor);
      }
    },
    /** Build tree structure from saved SceneTreeData including folders and hierarchy */
    buildTreeFromSavedData(savedData: SceneTreeData, sceneActors: ENGINE.Actor[], world: ENGINE.World) {
      // Create a map of actor UUIDs to actors for quick lookup
      const actorMap = new Map<string, ENGINE.Actor>();
      sceneActors.forEach(actor => {
        actorMap.set(actor.uuid, actor);
      });

      // Create scene root node
      const sceneNode = self.outliner.loadSceneRoot(world);
      DEBUG.logBuildOutliner && console.log(`[SceneStore] 🌳 Created scene root: ${sceneNode.name} (${sceneNode.id})`);
      const rootNodeId = sceneNode.id;

      // Recursive function to build tree nodes from saved data
      const buildNodeFromData = (nodeData: SceneTreeData, parentId?: string) => {
        if (nodeData.nodeType === 'scene') {
          for (const childData of nodeData.children ?? []) {
            buildNodeFromData(childData, sceneNode.id);
          }
          return sceneNode;
        }
        else if (nodeData.nodeType === 'folder') {
          // Create folder node
          const folderNode = self.outliner.addNode(SceneTreeNode.create({
            id: nodeData.id,
            name: nodeData.name ?? 'Folder',
            isExpanded: nodeData.isExpanded ?? undefined,
            nodeType: 'folder',
          }), parentId ?? rootNodeId);

          // Process children
          if (folderNode) {
            const folderId = folderNode.id;
            nodeData.children?.forEach(childData => {
              buildNodeFromData(childData, folderId);
            });
          }

          return folderNode;
        }
        else if (nodeData.nodeType === 'actor') {
          // Find the corresponding actor
          const actor = actorMap.get(nodeData.id);
          if (actor) {
            // Create actor node
            const actorNode = self.outliner.addNode(SceneTreeNode.create({
              id: nodeData.id,
              name: nameHelpers.getActorDisplayName(actor),
              isExpanded: nodeData.isExpanded ?? undefined,
              nodeType: 'actor',
            }), parentId ?? rootNodeId);

            // Initialize with actor data
            actorNode?.init(actor);

            DEBUG.logBuildOutliner && console.log(`[SceneStore] 🎭 Created actor node: ${nodeData?.id} (${actor?.name})`);

            // Recurse into nested children (actor-under-actor, folders, etc.) so load restores outliner hierarchy
            if (actorNode) {
              const actorNodeId = actorNode.id;
              nodeData.children?.forEach(childData => {
                buildNodeFromData(childData, actorNodeId);
              });
            }

            return actorNode;
          } else {
            console.warn(`[SceneStore] ⚠️ Actor not found for saved node: ${nodeData.id}`);
            return null;
          }
        }
        return null;
      };

      // Build the tree starting from root
      buildNodeFromData(savedData);
    },
    loadOutliner() {
      self.outliner.reset();

      const world = self.world;
      if (!world) return;

      const worldEditorData = world.editorData as EditorWorldData;
      const sceneTreeData = worldEditorData?.sceneTree;
      const sceneWorldActors = this.getSceneActors();

      // Saved tree data exists - load it
      if (sceneTreeData) {
        console.log('[SceneStore]📄 Loading outliner tree from saved data...');

        // Build tree structure from saved data
        this.buildTreeFromSavedData(sceneTreeData, sceneWorldActors, world);

        // Make sure save data is not missing worlds actors
        const savedActorIds = new Set<string>();
        function getSavedActorIds(node: SceneTreeData, outNodeIds: Set<string>) {
          if (node.nodeType === 'actor') outNodeIds.add(node.id);
          node.children?.forEach(child => getSavedActorIds(child, outNodeIds));
        }
        getSavedActorIds(sceneTreeData, savedActorIds);

        // Find missing actors (in scene but not in saved tree)
        const missingActors = sceneWorldActors.filter(actor => !savedActorIds.has(actor.uuid));
        if (missingActors.length > 0) {
          console.log(`[SceneStore]🔗 Adding ${missingActors.length} missing actors to outliner...`);
          this.addActorsToOutliner(missingActors);
        }
      }
      // No saved data - build default tree
      else {
        console.log('[SceneStore]🏗️ Building default outliner tree...');
        self.outliner.loadSceneRoot(world);
        this.addActorsToOutliner(sceneWorldActors);
      }
      actorParenting.rebuildParentingFromTree(self.outliner.rootNode, world);
      this.updateWorldDataWithOutliner();
    },
    /** Update outliner - sync with scene and refresh UI */
    updateOutliner() {
      const world = self.world;
      if (!world) return;

      // Ensure tree is initialized
      const rootNode = self.outliner.rootNode;
      if (!rootNode) {
        console.log('🔄 No root node, initializing outliner tree...');
        this.initOutliner();
        return;
      }
      const sceneWorldActors = this.getSceneActors();
      const worldActorIds = new Set(sceneWorldActors.map(actor => actor.uuid));
      const outlinerActorIds = self.outliner.actorNodeIds;

      const orphanOutlinerActorIds = Array.from(outlinerActorIds).filter(id => !worldActorIds.has(id));
      if (orphanOutlinerActorIds.length > 0) {
        self.outliner.removeNodes(orphanOutlinerActorIds);
      }

      // Find missing actors (in scene but not in scene tree)
      const refreshedOutlinerActorIds = self.outliner.actorNodeIds;
      const missingActors = sceneWorldActors.filter(actor => !refreshedOutlinerActorIds.has(actor.uuid));
      if (missingActors.length > 0) {
        this.addActorsToOutliner(missingActors);
      }
      self.outliner.pruneExpandedNodeIds();
      this.updateWorldDataWithOutliner();
    },
    updateWorldDataWithOutliner() {
      const world = self.world;
      const rootNode = self.outliner.rootNode;
      if (!world || !rootNode) return;
      function exportOutliner(node: ISceneTreeNode): SceneTreeData {
        return {
          id: node.id,
          name: node.nodeType === 'folder' ? node.name : undefined,
          nodeType: node.nodeType as 'scene' | 'folder' | 'actor',
          isExpanded: node.isExpanded ? undefined : false,
          children: node.children.length > 0 ? node.children.map(child => exportOutliner(child)) : undefined
        };
      }
      const worldEditorData = (world.editorData as EditorWorldData) || {};
      worldEditorData.sceneTree = exportOutliner(rootNode);
      world.editorData = worldEditorData;
    },
  }))
  // Teardown actions
  .actions(self => ({
    /** Clears editor state and unloads the active level; keeps the durable {@link gameLoop} session alive. */
    performLevelTeardown: flow(function* performLevelTeardownGen() {
      cancelDeferredInitialScreenshot();
      nameHelpers.resetActorNameCounters();
      actorParenting.clear();
      self.outliner.reset();

      while (self.selectedObjects.length > 0) {
        const selectedObject = self.selectedObjects[0];
        if (!selectedObject) break;
        selectedObject.setPropertyStore(undefined, false);
        detach(selectedObject);
      }

      self.transformControls = null;
      self.cachedSelectedActors = [];
      self.prefabReturnActorId = null;
      self.inspectorUpdateCounter = 0;
      self.missingAssetDiagnosticsEpoch = 0;
      self.isTranformingObjects = false;

      const world = self.world;
      const gameLoop = self.gameLoop;
      const renderer = self.renderer;
      const rootScene = gameLoop?.rootScene ?? null;

      self.world = null;
      self.scene = null;

      yield waitForRendererDrain(renderer);

      self.billboards.forEach(billboard => {
        objectHelpers.cleanupObjectIconBillboard(billboard);
      });
      self.billboards.clear();

      if (world) {
        world.scene.remove(self.selectionGroup);
        world.scene.remove(self.hiddenGroup);
      }

      if (gameLoop) {
        yield gameLoop.unloadCurrentLevel();
      } else if (world) {
        console.log('[SceneStore] destroyWorld (no game loop)', world.uuid);
        world.destroy();
      }

      if (rootScene) {
        renderer?.disposeSceneResources(rootScene);
      }
      if (world) {
        renderer?.disposeSceneResources(world.scene);
      }

      self.selectionGroup.clear();
      self.hiddenGroup.clear();
    }),
  }))
  .actions(self => ({
    /** Tears down the full editor session including the durable game loop. */
    performSessionTeardown: flow(function* performSessionTeardownGen() {
      yield self.performLevelTeardown();

      const renderer = self.renderer;
      const canvasScene = self.r3fCanvasScene;
      if (canvasScene) {
        renderer?.disposeSceneResources(canvasScene);
      }

      self.gameLoop?.destroy();
      self.gameLoop = null;

      self.camera = null;
      self.cameraControls = null;

      ENGINE.resourceManager.clearCachedResources();
      objectHelpers.evictObjectIconMaterialsAfterResourceClear();
    }),
  }))
  .actions(self => ({
    /** @deprecated Use {@link performLevelTeardown} or {@link performSessionTeardown}. */
    performWorldTeardown: flow(function* performWorldTeardownGen() {
      yield self.performSessionTeardown();
    }),
  }))
  // World actions
  .actions(self => ({
    setRendererType(type: ENGINE.RendererType) {
      DEBUG.logSetRenderer && console.log('[SceneStore] setRendererType', type);
      self.rendererType = type;
      self.rendererTypeResolved = true;
    },
    setRenderer(renderer: ENGINE.IGenesysRenderer | null) {
      DEBUG.logSetRenderer && console.log('[SceneStore] setRenderer', renderer == null ? null : (get(renderer, 'isWebGPURenderer') ? 'WebGPURenderer' : 'WebGLRenderer'));
      self.renderer = renderer;
    },
    setR3fCanvasScene(scene: THREE.Scene | null) {
      self.r3fCanvasScene = scene;
    },
    setCapturingViewportScreenshot(value: boolean) {
      self.isCapturingViewportScreenshot = value;
    },
    setCamera(camera: THREE.Camera | null) {
      DEBUG.logSetCamera && console.log('[SceneStore] setCamera', camera == null ? null : { uuid: camera.uuid, type: camera.type });
      self.camera = camera;
    },
    setCameraControls(controls: THREE.EventDispatcher | null): boolean {
      if (controls === self.cameraControls) return false;
      DEBUG.logSetCameraControls && console.log('[SceneStore] setCameraControls', controls == null ? null : invoke(controls, 'toJSON'));
      self.cameraControls = controls;
      return true;
    },
    saveCameraState(name: CameraName, captureScreenshot: boolean = false, viewModeOverride?: ViewMode) {
      if (self.isPrefabMode && name !== 'prefab') return;
      const world = self.world;
      const controls = self.cameraControls;
      if (world && controls && (controls instanceof CameraControlsImpl)) {
        if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
          const viewMode = viewModeOverride ?? editorStore.viewMode;
          DEBUG.logSaveCameraState && console.log('[SceneStore] saveCameraState', viewMode);
          savePersistedCameraState({
            context: {
              projectName: editorStore.project.projectName,
              sceneName: editorStore.project.sceneName,
              cameraName: name,
            },
            controls,
            viewMode,
            flySpeed: editorStore.cameraFlySpeed,
          });

          // Capture and save screenshot
          if (captureScreenshot) {
            this.captureScreenshot();
          }
        }
      }
    },
    captureScreenshot(scenePathOverride?: string | null, imageType: string = 'image/jpeg', imageQuality: number = 0.9) {
      if (DEBUG.logCaptureScreenshot) console.log('📸 Capture screenshot...');

      if (self.isPrefabMode) {
        if (DEBUG.logCaptureScreenshot) console.log('⚠️ Skipping screenshot while editing a prefab (avoids overwriting the level scene thumbnail).');
        return;
      }

      const renderer = self.renderer;
      const scene = self.scene;
      const camera = self.camera;
      const world = self.world;

      if (!renderer || !scene || !camera || !world) {
        if (DEBUG.logCaptureScreenshot) console.log('⚠️ No renderer, scene, camera, or world available for screenshot.');
        return;
      }

      const projectFolder = editorStore.project.projectFolder;
      const projectName = editorStore.project.projectName;
      const scenePath = normaliseSceneAssetPath(scenePathOverride ?? editorStore.project.currentScene, projectFolder);
      if (!projectFolder || !projectName) {
        if (DEBUG.logCaptureScreenshot) console.log('⚠️ No project folder or name for screenshot.');
        return;
      }

      // Use project folder as unique key (normalized path)
      const projectKey = getProjectScreenshotIdbKey(projectFolder);
      if (!projectKey) {
        if (DEBUG.logCaptureScreenshot) console.log('⚠️ Could not derive project key for screenshot.');
        return;
      }
      const sceneScreenshotKey = getProjectScopedSceneScreenshotIdbKey(projectFolder, scenePath);

      if (DEBUG.logCaptureScreenshot) {
        console.log('📸 Screenshot capture details:', {
          projectFolder,
          projectName,
          projectKey,
        });
      }

      try {
        // Use the lastRenderer from postProcessManager (already rendered by SceneRenderer)
        const postProcessManager = world.postProcessManager;
        const canvas = get(postProcessManager, 'lastRenderer.domElement') ?? renderer.domElement;
        if (!canvas) {
          if (DEBUG.logCaptureScreenshot) console.log('⚠️ No canvas found for screenshot.');
          return;
        }

        // Render a clean frame with editor objects (gizmos, grid, billboards, etc.) hidden,
        // so the captured screenshot contains only game-world content.
        // Guard with isCapturingViewportScreenshot to prevent re-entrant renderCleanFrame
        // calls if this is invoked from within an rAF (e.g. a MobX reaction mid-frame).
        if (postProcessManager && !self.isCapturingViewportScreenshot) {
          const domElement = renderer.domElement;
          const width = domElement.clientWidth || domElement.width;
          const height = domElement.clientHeight || domElement.height;
          self.isCapturingViewportScreenshot = true;
          try {
            postProcessManager.renderCleanFrame(renderer, scene, camera, { width, height });
          } finally {
            self.isCapturingViewportScreenshot = false;
          }
        }

        // Capture canvas as blob
        canvas.toBlob(async blob => {
          if (!blob) {
            if (DEBUG.logCaptureScreenshot) console.log('⚠️ Failed to create screenshot blob.');
            return;
          }

          if (DEBUG.logCaptureScreenshot) {
            console.log('💾 Saving screenshot blob:', { projectKey, size: blob.size, type: blob.type });
          }

          // Two entries are saved to IndexedDB, both storing the same blob:
          //   1. Per-project (projectKey): used by the dashboard to show the project thumbnail.
          //   2. Per-scene  (sceneScreenshotKey): used by the asset browser to show individual scene thumbnails.
          try {
            const db = await openIndexedDB();

            // 1. Per-project thumbnail — keyed by the project folder path.
            await saveToIndexedDB(db, projectKey, blob);

            // 2. Per-scene thumbnail — keyed by project + normalised scene asset path.
            if (scenePath && sceneScreenshotKey) {
              await saveToIndexedDB(db, sceneScreenshotKey, blob);
              if (DEBUG.logCaptureScreenshot) console.log('✅ Scene screenshot saved to IndexedDB:', sceneScreenshotKey);

              // Refresh thumbnail for the scene asset to trigger UI update
              const sceneAsset = editorStore.assets.assetTree.findFile(scenePath);
              if (sceneAsset) {
                sceneAsset.refreshThumbnail();
                if (DEBUG.logCaptureScreenshot) console.log('✅ Scene asset thumbnail refreshed:', scenePath);
              }
            }

            db.close();
            if (DEBUG.logCaptureScreenshot) console.log('✅ Screenshot saved to IndexedDB:', projectKey);
          } catch (error) {
            console.error('❌ Failed to save screenshot to IndexedDB:', error);
          }
        }, imageType, imageQuality);
      } catch (error) {
        console.error('❌ Failed to capture screenshot:', error);
      }
    },
    async captureScreenshotAsync(
      scenePathOverride?: string | null,
      imageType: string = 'image/jpeg',
      imageQuality: number = 0.9,
      includeImage: boolean = false,
      filename?: string | null,
      captureWidth?: number | null,
      captureHeight?: number | null,
      updateThumbnails: boolean = false,
    ): Promise<CaptureScreenshotAsyncResult> {
      const emptyResult = (
        overrides: Partial<CaptureScreenshotAsyncResult>,
      ): CaptureScreenshotAsyncResult => ({
        ok: false,
        scenePath: null,
        projectScreenshotKey: null,
        sceneScreenshotKey: null,
        projectScreenshotPath: null,
        sceneScreenshotPath: null,
        screenshotPath: null,
        requestedFilename: null,
        captureWidth: null,
        captureHeight: null,
        imageType,
        imageBase64: null,
        imageByteLength: null,
        warnings: [],
        ...overrides,
      });

      if (self.isPrefabMode) {
        return emptyResult({
          warnings: ['Screenshots are blocked while editing a prefab.'],
        });
      }

      const renderer = self.renderer;
      const scene = self.scene;
      const camera = self.camera;
      const world = self.world;

      if (!renderer || !scene || !camera || !world) {
        return emptyResult({
          warnings: ['Renderer, scene, camera, or world is not available for screenshot capture.'],
        });
      }

      const projectFolder = editorStore.project.projectFolder;
      const projectName = editorStore.project.projectName;
      const scenePath = normaliseSceneAssetPath(scenePathOverride ?? editorStore.project.currentScene, projectFolder);
      if (!projectFolder || !projectName) {
        return emptyResult({
          scenePath: scenePath ?? null,
          warnings: ['Project folder or name is not available for screenshot capture.'],
        });
      }

      const projectKey = getProjectScreenshotIdbKey(projectFolder);
      if (!projectKey) {
        return emptyResult({
          scenePath: scenePath ?? null,
          warnings: ['Could not derive project key for screenshot capture.'],
        });
      }
      const sceneScreenshotKey = getProjectScopedSceneScreenshotIdbKey(projectFolder, scenePath);

      const hasCustomDimensions = captureWidth != null && captureHeight != null;

      // Prevent EditorLoop from rendering a concurrent frame while we capture.
      // The flag is non-observable (volatile), so it never triggers MobX reactions.
      this.setCapturingViewportScreenshot(true);
      // Declared outside try so the finally block can dispose it.
      let offscreenRenderer: ENGINE.IGenesysRenderer | null = null;
      try {
        // Wait for any in-flight EditorLoop frame to finish before we touch the renderer.
        await waitForNextFrame();

        const postProcessManager = world.postProcessManager;

        let captureCanvas: HTMLCanvasElement;

        if (hasCustomDimensions) {
          // For custom dimensions: create an isolated offscreen renderer so the main
          // viewport canvas is never resized. This prevents R3F size tracking from
          // desyncing during the async toBlob yield.
          const offscreenCanvas = document.createElement('canvas');
          offscreenCanvas.width = captureWidth!;
          offscreenCanvas.height = captureHeight!;

          try {
            if (renderer.asWebGPU()) {
              offscreenRenderer = await ENGINE.GenesysWebGPURenderer.create({
                canvas: offscreenCanvas,
                antialias: false,
                alpha: false,
                depth: true,
                stencil: false,
                powerPreference: 'high-performance',
              });
            } else {
              offscreenRenderer = await ENGINE.GenesysWebGLRenderer.create({
                canvas: offscreenCanvas,
                antialias: false,
                alpha: false,
                depth: true,
                stencil: false,
                // preserveDrawingBuffer required on WebGL so toBlob can read the canvas.
                preserveDrawingBuffer: true,
                powerPreference: 'high-performance',
              });
            }
          } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            return emptyResult({
              scenePath: scenePath ?? null,
              projectScreenshotKey: projectKey,
              sceneScreenshotKey,
              warnings: [`Failed to create offscreen renderer for custom-dimension capture: ${message}`],
            });
          }

          offscreenRenderer.setSize(captureWidth!, captureHeight!);
          offscreenRenderer.setPixelRatio(1);

          // Hide excluded editor-only objects (gizmos, grid, billboards, etc.) so
          // the capture contains only game-world content — mirrors renderCleanFrame.
          const excludedObjects = postProcessManager?.getExcludedObjects() ?? new Set<THREE.Object3D>();
          const toRestore: THREE.Object3D[] = [];
          for (const obj of excludedObjects) {
            if (obj.visible) {
              obj.visible = false;
              toRestore.push(obj);
            }
          }
          try {
            offscreenRenderer.render(scene, camera);
          } finally {
            for (const obj of toRestore) obj.visible = true;
          }

          // Flush GPU commands before reading the canvas.
          await waitForRendererDrain(offscreenRenderer);

          captureCanvas = offscreenCanvas;
        } else {
          // For viewport dimensions: use postProcessManager's output canvas when available.
          captureCanvas = (get(postProcessManager, 'lastRenderer.domElement') ?? renderer.domElement) as unknown as HTMLCanvasElement;
        }

        if (!captureCanvas) {
          return emptyResult({
            scenePath: scenePath ?? null,
            projectScreenshotKey: projectKey,
            sceneScreenshotKey,
            warnings: ['No canvas found for screenshot capture.'],
          });
        }

        if (!hasCustomDimensions && postProcessManager) {
          // For viewport-size captures: render a clean frame on the main renderer
          // (editor gizmos/grid hidden). Custom-dimension path already rendered via
          // the offscreen renderer above.
          const domElement = renderer.domElement;
          const w = domElement.clientWidth || domElement.width;
          const h = domElement.clientHeight || domElement.height;
          postProcessManager.renderCleanFrame(renderer, scene, camera, { width: w, height: h });

          // Flush the GPU command queue so toBlob reads committed pixels.
          await waitForRendererDrain(renderer);
        }

        const blob = await new Promise<Blob | null>((resolve) => {
          captureCanvas.toBlob(resolve, imageType, imageQuality);
        });
        if (!blob) {
          return emptyResult({
            scenePath: scenePath ?? null,
            projectScreenshotKey: projectKey,
            sceneScreenshotKey,
            warnings: ['Failed to create screenshot blob.'],
          });
        }

        const actualCaptureWidth = captureCanvas.width;
        const actualCaptureHeight = captureCanvas.height;
        const warnings: string[] = [];

        // IndexedDB thumbnail writes — only when updateThumbnails is requested.
        if (updateThumbnails) {
          try {
            const db = await openIndexedDB();
            await saveToIndexedDB(db, projectKey, blob);
            if (scenePath && sceneScreenshotKey) {
              await saveToIndexedDB(db, sceneScreenshotKey, blob);
              const sceneAsset = editorStore.assets.assetTree.findFile(scenePath);
              sceneAsset?.refreshThumbnail();
            }
            db.close();
          } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            warnings.push(`Failed to save screenshot to IndexedDB: ${message}`);
          }
        }

        // Primary export: always save a named, collision-safe file.
        let screenshotPath: string | null = null;
        let exportProjectPath: string | null = null;
        let exportScenePath: string | null = null;
        const sanitizedFilename = filename?.trim() ? sanitizeScreenshotFilename(filename) : null;
        try {
          const storageProvider = editorStore.project.makeScopedStorageProvider();
          await storageProvider.createDirectory(ENGINE.AssetPath.fromString(PROJECT_SCREENSHOTS_DIR));

          const ext = getScreenshotExtensionFromMimeType(imageType);
          let exportStem: string;
          if (sanitizedFilename) {
            exportStem = sanitizedFilename;
          } else {
            const sceneStem = scenePath ? getSceneScreenshotStem(scenePath) : 'screenshot';
            exportStem = `${sceneStem}-${buildTimestampSuffix()}`;
          }

          const exportPath = await buildCollisionSafeExportPath(storageProvider, exportStem, ext);
          await storageProvider.uploadFile(ENGINE.AssetPath.fromString(exportPath), blob, { contentType: imageType });
          screenshotPath = exportPath;

          // Thumbnail files — only when updateThumbnails is requested.
          if (updateThumbnails) {
            const projectPath = getProjectScreenshotAssetPath(imageType);
            await storageProvider.uploadFile(ENGINE.AssetPath.fromString(projectPath), blob, { contentType: imageType });
            exportProjectPath = projectPath;
            if (scenePath) {
              const sceneAssetPath = getSceneScreenshotAssetPath(scenePath, imageType);
              await storageProvider.uploadFile(ENGINE.AssetPath.fromString(sceneAssetPath), blob, { contentType: imageType });
              exportScenePath = sceneAssetPath;
            }
          }
        } catch (error) {
          const message = error instanceof Error ? error.message : String(error);
          warnings.push(`Failed to save screenshot to project assets: ${message}`);
        }

        let imageBase64: string | null = null;
        if (includeImage) {
          try {
            imageBase64 = await blobToBase64(blob);
          } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            warnings.push(`Failed to encode screenshot for inline return: ${message}`);
          }
        }

        const ok = screenshotPath != null;
        if (!ok) {
          warnings.push('Screenshot was not saved to the project screenshots folder.');
        }

        return {
          ok,
          scenePath: scenePath ?? null,
          projectScreenshotKey: updateThumbnails ? projectKey : null,
          sceneScreenshotKey: updateThumbnails ? sceneScreenshotKey : null,
          projectScreenshotPath: exportProjectPath,
          sceneScreenshotPath: exportScenePath,
          screenshotPath,
          requestedFilename: sanitizedFilename,
          captureWidth: actualCaptureWidth,
          captureHeight: actualCaptureHeight,
          imageType,
          imageBase64,
          imageByteLength: blob.size,
          warnings,
        };
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        return emptyResult({
          scenePath: scenePath ?? null,
          projectScreenshotKey: projectKey,
          sceneScreenshotKey,
          warnings: [`Failed to capture screenshot: ${message}`],
        });
      } finally {
        // Dispose the offscreen renderer used for custom-dimension captures.
        if (offscreenRenderer) {
          try {
            offscreenRenderer.dispose();
          } catch {
            // Ignore disposal errors.
          }
        }
        // Defensive renderer state reset: ensure no stale render target or viewport
        // is left on the main renderer before EditorLoop resumes.
        try {
          const nativeRenderer = renderer.asWebGL() ?? renderer.asWebGPU();
          if (nativeRenderer) {
            nativeRenderer.setRenderTarget(null);
            const sz = nativeRenderer.getSize(new THREE.Vector2());
            nativeRenderer.setViewport(0, 0, sz.width, sz.height);
          }
        } catch {
          // Renderer may have been disposed during capture — ignore.
        }
        this.setCapturingViewportScreenshot(false);
      }
    },
    clearCameraState(name: CameraName, viewModeOverride?: ViewMode) {
      if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
        return;
      }
      const viewMode = viewModeOverride ?? editorStore.viewMode;
      clearPersistedCameraState({
        context: {
          projectName: editorStore.project.projectName,
          sceneName: editorStore.project.sceneName,
          cameraName: name,
        },
        viewMode,
      });
    },
    loadCameraState(
      name: CameraName,
      viewModeOverride?: ViewMode,
      controlsOverride?: CameraControlsImpl | null,
    ): { flySpeed: number } {
      const world = self.world;
      const controls = controlsOverride ?? self.cameraControls;
      if (world && controls && (controls instanceof CameraControlsImpl)) {
        if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
          const viewMode = viewModeOverride ?? editorStore.viewMode;
          DEBUG.logLoadCameraState && console.log('[SceneStore] loadCameraState', viewMode);
          const cameraControls = controls;
          const { flySpeed, viewState } = loadPersistedCameraState({
            context: {
              projectName: editorStore.project.projectName,
              sceneName: editorStore.project.sceneName,
              cameraName: name,
            },
            viewMode,
          });
          editorStore.setCameraFlySpeed(flySpeed, 1);

          const [positionX, positionY, positionZ] = viewState.position;
          const [targetX, targetY, targetZ] = viewState.target;
          cameraControls.setLookAt(positionX, positionY, positionZ, targetX, targetY, targetZ, false);
          if (cameraControls.camera instanceof THREE.OrthographicCamera) {
            cameraControls.zoomTo(viewState.zoom, false);
          }
          cameraControls.enabled = true;
          return { flySpeed };
        }
      }
      return { flySpeed: CAMERA_STATE_DEFAULT_FLY_SPEED };
    },
  }))
  .actions(self => ({
    ensureEditorGameLoop: flow(function* ensureEditorGameLoopGen(): Generator<any, EditorGameLoop, any> {
      if (self.gameLoop) {
        return self.gameLoop;
      }

      if (!self.renderer) {
        console.log('[SceneStore] Waiting for renderer initialization...');
        yield new Promise<void>((resolve) => {
          const dispose = autorun(() => {
            if (self.renderer) {
              dispose();
              resolve();
            }
          });
        });
      }

      const renderer = self.renderer;
      if (!renderer) {
        throw new Error('Missing call to initRenderer');
      }

      const gameLoop = new EditorGameLoop(document.body);
      gameLoop.initSession(renderer);
      self.gameLoop = gameLoop;
      console.log('[SceneStore] Editor game loop session initialized');
      return gameLoop;
    }),
  }))
  .actions(self => ({
    createWorld: flow(function* createWorldAsync(worldData?: ENGINE.SerializedData): Generator<any, ENGINE.World | null, any> {
      const currentWorld = self.world;

      if (currentWorld) {
        console.warn('World is already created');
        return currentWorld;
      }

      if (self.isCreating) {
        console.warn('World is already being created');
        return currentWorld;
      }

      if (DEBUG_SKIP_LOAD_EDITOR_WORLD) {
        console.log('[SceneStore] DEBUG_SKIP_EDITOR_WORLD is enabled, skipping world creation.');
        self.isCreating = false;
        return null;
      }

      try {
        self.isCreating = true;
        nameHelpers.resetActorNameCounters();

        const gameLoop = yield self.ensureEditorGameLoop();
        const loadTarget: { world: ENGINE.World | null } = { world: null };

        const hooks: EditorWorldActorHooks = {
          onActorAddToWorld: async (actor: ENGINE.Actor) => {
            try {
              const billboard = await objectHelpers.createActorBillboardIcon(actor);
              if (self.world && loadTarget.world && self.world !== loadTarget.world) {
                if (billboard) {
                  objectHelpers.cleanupObjectIconBillboard(billboard);
                }
                return;
              }
              if (billboard) {
                billboard.group.visible = editorStore.showObjectIcons;
                actor.rootComponent?.add(billboard.group);
                self.billboards.set(actor.uuid, billboard);
              }
              objectHelpers.noteActorAddedToEditorWorld(actor);
              if (objectHelpers.isSceneEnvironmentActor(actor)) {
                objectHelpers.applySkyboxViewportVisibilityForActor(actor);
              } else if ((actor.editorData as EditorObjectData).hidden) {
                objectHelpers.hideActorInEditor(actor, true);
              }
            } catch (error) {
              console.error('[SceneStore] Failed to run OnActorAddToWorld callback:', error);
            }
          },
          onActorRemoveFromWorld: (actor: ENGINE.Actor) => {
            const billboard = self.billboards.get(actor.uuid);
            if (billboard) {
              objectHelpers.cleanupObjectIconBillboard(billboard);
              self.billboards.delete(actor.uuid);
            }
          },
        };

        console.log('[SceneStore] Loading editor level via EditorGameLoop');
        const newWorld: ENGINE.World = yield gameLoop.loadEditorLevel(
          worldData,
          hooks,
          editorAssets.prefabDataCopy,
        );
        loadTarget.world = newWorld;

        const rootScene = gameLoop.rootScene;
        if (!rootScene) {
          throw new Error('[SceneStore] Editor game loop root scene is missing after level load');
        }

        if (self.world) {
          throw new Error('Old world not destroyed');
        }

        self.world = newWorld;
        self.scene = rootScene;
        console.log('[SceneStore] Created World', newWorld.uuid, 'RootScene', rootScene.uuid);

        newWorld.scene.add(self.selectionGroup);
        newWorld.scene.add(self.hiddenGroup);
        self.hiddenGroup.visible = false;

        const navigationServer = gameLoop.navigationServer;
        if (navigationServer) {
          console.log('[SceneStore] Navigation server is available:', navigationServer.constructor.name);
        }

        return newWorld;
      } catch (error) {
        console.error('Error creating editor world:', error);
        self.world = null;
        self.scene = null;
        throw error;
      } finally {
        self.isCreating = false;
      }
    }),
    destroyWorld: flow(function* destroyWorldAsync() {
      yield self.performSessionTeardown();
      runInAction(() => editorStore.inspectorPanel.clearPropertyStoreCache());
    }),
    setPlayModeActive(active: boolean) {
      self.isPlayMode = active;
    },
    setPlayModeStarting(starting: boolean) {
      self.isPlayModeStarting = starting;
      syncPlayModeStartingGate(starting);
    },
    setBackgroundColor(color: string) {
      self.backgroundColor = color;
    },
    applySkyboxViewportVisibility() {
      objectHelpers.applySkyboxViewportVisibility(self.world);
    },
  }))
  .actions(self => ({
    afterCreate() {
      addDisposer(self, autorun(() => {
        if (self.isPrefabMode) {
          self.setBackgroundColor(self.isEngineAsset ? SceneBackgroundColors.engine : SceneBackgroundColors.prefab);
        } else {
          self.setBackgroundColor(SceneBackgroundColors.default);
        }
      }));

      addDisposer(self, reaction(
        () => self.isPrefabMode,
        () => {
          if (self.isResettingWorld) {
            return;
          }
          objectHelpers.applySkyboxViewportVisibility(self.world);
        },
      ));

      // Broadcast onEditorComponentSelectionChanged to all components of the
      // selected actor when the intra-actor component selection changes.
      addDisposer(self, reaction(
        () => {
          const obj = self.selectedObject;
          return {
            selectedComponent: (obj?.hierarchy.selectedNode?.asComponent ?? null) as ENGINE.SceneComponent | null,
            selectedActors: self.selectedActors,
            actor: obj?.asActor ?? null,
          };
        },
        ({ selectedComponent, selectedActors, actor }) => {
          if (actor) {
            try {
              actor.onEditorComponentSelectionChanged(selectedComponent, selectedActors);
            } catch (error) {
              reportEditorHookError({
                hook: 'onEditorComponentSelectionChanged',
                context: 'SceneStore.afterCreate.reaction',
                target: actor,
                error,
              });
            }
          }
        },
        { fireImmediately: false, equals: (a, b) => a.selectedComponent === b.selectedComponent && a.actor === b.actor },
      ));
    }
  }))
  .actions(self => ({
    resetWorld: flow(function* resetWorldAsync(
      worldData?: ENGINE.SerializedData,
      options: { resetCanvas?: boolean } = {}
    ) {
      console.log('[SceneStore] resetWorld');
      self.isResettingWorld = true;
      const { resetCanvas = false } = options;

      try {
        yield self.performLevelTeardown();
        runInAction(() => editorStore.inspectorPanel.clearPropertyStoreCache());
        if (resetCanvas) {
          self.renderer = null;
          self.camera = null;
          self.cameraControls = null;
          self.gameLoop?.destroy();
          self.gameLoop = null;
          self.canvasResetVersion += 1;
        }
        yield waitForNextFrame();
        return yield self.createWorld(worldData);
      } finally {
        self.isResettingWorld = false;
      }
    })
  }))
  .actions(self => ({
    newScene: flow(function* newSceneAsync(worldData?: ENGINE.SerializedData, backgroundColor?: THREE.ColorRepresentation) {
      const world = yield self.resetWorld(worldData);
      if (world) {
        ENGINE.GameBuilder.createDefaultEditorScene(world);
        self.loadOutliner();
        scheduleDeferredInitialScreenshotIfMissing();
      }
      return world;
    })
  }))
  // Object selection and manipulation actions
  .actions(self => ({
    detachSelectedObjectAt(index: number) {
      const selectedObject = self.selectedObjects[index];
      if (!selectedObject) return;
      if (isAlive(selectedObject)) {
        // Clear cross-tree references before detaching to avoid unresolved MST refs.
        selectedObject.setPropertyStore(undefined, false);
        detach(selectedObject);
      } else {
        self.selectedObjects.splice(index, 1);
      }
    },
    clearSelectedObjects() {
      while (self.selectedObjects.length > 0) {
        this.detachSelectedObjectAt(0);
      }
    },
    /** Select/deselect object or select one object at a time with multiselect.  */
    selectObject(target: ENGINE.Actor | THREE.Object3D | null | string, multiSelect: boolean = false) {
      runInAction(() => {
        editorStore.inspectorPanel.setShowSceneSettingsView(false);
        editorStore.clearSceneSettingsPreviousTargetSnapshot();

        const world = self.world;
        if (!world || !target) {
          this.clearSelectedObjects();
          self.outliner.selectNode(null, false);
          self.updateSelectionGroup();
          return;
        }

        if (DEBUG.logSelectedObjects) {
          if (typeof target === 'string') {
            console.log('[SceneStore] selectObject', { prefabPath: target });
          } else {
            const actor = objectHelpers.findActorFromTarget(target);
            console.log('[SceneStore] selectObject', actor ? actorLogRef(actor) : object3DLogRef(target as THREE.Object3D));
          }
        }

        const actor = typeof target === 'string'
          ? prefabActions.loadPrefabAsset(target)
          : objectHelpers.findActorFromTarget(target);

        if (actor) {
          editorStore.clearPreviousSelectionSnapshot();
          const targetId = actor.uuid;

          // Check if already selected
          const existingIndex = self.selectedObjects.findIndex(obj => obj.id === targetId);
          const isAlreadySelected = existingIndex !== -1;

          // Handle multi-select toggling if already selected
          if (multiSelect && isAlreadySelected) {
            this.detachSelectedObjectAt(existingIndex);
            self.outliner.selectNode(targetId, false);
            self.updateSelectionGroup();
            return;
          }

          // Clear previous selection if not multi-selecting and not already selected
          if (!multiSelect) {
            if (isAlreadySelected) {
              return; // Skip
            }
            this.clearSelectedObjects();
            // Also clear outliner selection when not multi-selecting
            self.outliner.selectedNodes.forEach(node => node.setSelected(false));
            self.outliner.selectedNodes.clear();
          }

          // Only add if not already selected
          if (!isAlreadySelected) {
            const outlinerNode = self.outliner.getNodeById(targetId);
            const selectedObject = EditableObject.create({
              id: targetId,
              name: outlinerNode?.name ?? nameHelpers.getActorDisplayName(actor)
            });
            selectedObject.init(actor);
            self.selectedObjects.push(selectedObject);
          }

          // New selected
          if (!isAlreadySelected) {
            // Update outliner selection
            self.outliner.selectNode(targetId, multiSelect);
          }

          self.updateSelectionGroup();
          return actor;
        }
      });
      return null;
    },
    deselectObjects() {
      // Clear all current selections
      this.clearSelectedObjects();
      self.outliner.deselectActorNodes();
      self.updateSelectionGroup();
    },
    updateInspector() {
      self.inspectorUpdateCounter++;
    },
    bumpMissingAssetDiagnosticsEpoch() {
      self.missingAssetDiagnosticsEpoch++;
    },
    // Select multiple objects at once, if multiSelect is false, it will replace any existing selection.
    selectObjects(targets: (ENGINE.Actor | THREE.Object3D)[] | null, multiSelect: boolean = false, fromOutliner: boolean = false) {
      runInAction(() => {
        editorStore.inspectorPanel.setShowSceneSettingsView(false);
        editorStore.clearSceneSettingsPreviousTargetSnapshot();

        const world = self.world;
        if (!world || !targets || targets.length === 0) {
          this.deselectObjects();
          return;
        }

        const resolvedTargets: { actor: ENGINE.Actor; id: string }[] = [];
        const resolvedTargetIds = new Set<string>();
        for (const target of targets) {
          const actor = objectHelpers.findActorFromTarget(target);
          if (!actor || resolvedTargetIds.has(actor.uuid)) continue;
          resolvedTargetIds.add(actor.uuid);
          resolvedTargets.push({ actor, id: actor.uuid });
        }

        if (resolvedTargets.length === 0) {
          this.deselectObjects();
          return;
        }

        editorStore.clearPreviousSelectionSnapshot();

        // Get target IDs to compare with current selection
        const targetIds = resolvedTargets.map(target => target.id);
        const currentSelectedIds = self.selectedObjects.map(obj => obj.id);

        // Check if selection is actually changing
        const currentIds = [...currentSelectedIds].sort();
        const newIds = [...targetIds].sort();
        const isSameSelection = currentIds.length === newIds.length &&
          currentIds.every((id, index) => id === newIds[index]);

        // If selection is the same, don't do anything
        if (isSameSelection) {
          return;
        }

        if (DEBUG.logSelectedObjects) {
          console.log('[SceneStore] selectObjects', targets.map(t => {
            const actor = objectHelpers.findActorFromTarget(t);
            return actor ? actorLogRef(actor) : object3DLogRef(t as THREE.Object3D);
          }));
        }

        // Build map of existing objects for reuse
        const existingObjectsMap = new Map(
          self.selectedObjects.map(obj => [obj.id, obj])
        );

        // Determine what to add/remove
        const currentIdSet = new Set(existingObjectsMap.keys());
        const newIdSet = new Set(targetIds);

        if (!multiSelect) {
          // Remove objects not in new selection
          for (const id of currentIdSet) {
            if (!newIdSet.has(id)) {
              const idx = self.selectedObjects.findIndex(o => o.id === id);
              if (idx !== -1) this.detachSelectedObjectAt(idx);
            }
          }
        }

        // Add new objects (reuse existing or create new)
        resolvedTargets.forEach(({ actor, id: targetId }) => {
          // Skip if this object is already in our selection
          if (existingObjectsMap.has(targetId)) return;

          // Create and add the object to selection
          const outlinerNode = self.outliner.getNodeById(targetId);
          const selectedObject = EditableObject.create({
            id: targetId,
            name: outlinerNode?.name ?? nameHelpers.getActorDisplayName(actor)
          });
          selectedObject.init(actor);
          self.selectedObjects.push(selectedObject);
          existingObjectsMap.set(targetId, selectedObject);
        });

        if (!fromOutliner) {
          const nextOutlinerIds = multiSelect
            ? [...currentSelectedIds, ...targetIds.filter(id => !currentIdSet.has(id))]
            : targetIds;
          self.outliner.setSelectedNodeIds(nextOutlinerIds);
        }

        self.updateSelectionGroup();
      });
    },
    // Select the scene root (world) to show world-level properties
    selectSceneRoot(): void {
      editorStore.inspectorPanel.setShowSceneSettingsView(false);
      editorStore.clearSceneSettingsPreviousTargetSnapshot();

      const world = self.world;
      if (!world) {
        this.clearSelectedObjects();
        self.outliner.selectNode(null, false);
        self.updateSelectionGroup();
        return;
      }

      // Skip if scene root is already selected
      const rootNode = self.outliner.rootNode;
      if (!rootNode) {
        if (DEBUG.logSelectedObjects) console.log('[SceneStore] selectSceneRoot - no root node found');
        return;
      }

      if (self.outliner.selectedNodeId === rootNode.id &&
        self.selectedObjects.length === 1 &&
        self.selectedObject?.selectedSubobjectTarget === world) {
        if (DEBUG.logSelectedObjects) console.log('[SceneStore] selectSceneRoot - already selected, skipping');
        return;
      }

      if (DEBUG.logSelectedObjects) console.log('[SceneStore] selectSceneRoot', rootNode.id);

      // Clear previous selection
      this.clearSelectedObjects();
      self.outliner.selectedNodes.forEach(node => node.setSelected(false));
      self.outliner.selectedNodes.clear();

      // Create a special EditableObject for the scene root
      const sceneRootObject = EditableObject.create({
        id: rootNode.id,
        name: rootNode.name || 'Scene'
      });

      // Initialize with the world instead of an actor
      sceneRootObject.initWithWorld(world);
      self.selectedObjects.push(sceneRootObject);

      // Update outliner selection
      self.outliner.selectNode(rootNode.id, false);
      self.updateSelectionGroup();
    },
    removeObjects(...targets: (ENGINE.Actor | THREE.Object3D | null | undefined)[]) {
      removeObjectsFromWorld(
        self.world,
        targets,
        self.outliner,
        () => this.selectObject(null),
        () => self.updateOutliner()
      );
    },
    makeObjectRoot(targetComponent: ENGINE.SceneComponent) {
      const world = self.world;
      if (!world) return;

      // Validate the target component
      if (!targetComponent || !(targetComponent instanceof ENGINE.SceneComponent)) {
        toast.warning('Target is not a scene component');
        return;
      }

      // Find the actor that owns this component
      const actor = targetComponent.getActor();
      if (!actor) {
        toast.warning('No actor found for target component');
        return;
      }

      // Cache the current root component of the actor
      const currentRootComponent = actor.rootComponent;
      if (!currentRootComponent) {
        toast.warning('Actor has no root component');
        return;
      }

      if (targetComponent === currentRootComponent) {
        toast.info('Target component is already the root component');
        return;
      }

      // Check if the target component is a descendant of the current root
      let isDescendant = false;
      currentRootComponent.traverse((child) => {
        if (child === targetComponent) {
          isDescendant = true;
        }
      });

      if (!isDescendant) {
        toast.warning('Target component is not part of the actor hierarchy');
        return;
      }

      const undoRedo = getSceneUndoRedo();

      // Build durable accessors outside the closures — UniversalAccessor stores IDs,
      // not live references, so it is safe to reuse across do/undo invocations.
      const actorAccessor = UniversalAccessor.forActor(actor.uuid);
      const targetAccessor = UniversalAccessor.forComponent(actor.uuid, targetComponent.uuid);
      const prevRootAccessor = UniversalAccessor.forComponent(actor.uuid, currentRootComponent.uuid);
      const originalParentAccessor = targetComponent.parent
        ? UniversalAccessor.forComponent(actor.uuid, targetComponent.parent.uuid)
        : null;
      const originalIndex = targetComponent.parent ? targetComponent.parent.children.indexOf(targetComponent) : -1;
      const originalRootTransform = {
        position: currentRootComponent.position.clone(),
        quaternion: currentRootComponent.quaternion.clone(),
        scale: currentRootComponent.scale.clone()
      };

      void undoRedo.recordAction(
        'Make Root Component',
        () => {
          const doActor = actorAccessor.resolveActor();
          if (!doActor) return;
          const doTarget = targetAccessor.resolveComponent();
          if (!doTarget) return;
          const doPrevRoot = prevRootAccessor.resolveComponent();
          if (!doPrevRoot) return;

          doTarget.removeFromParent();
          doActor.setRootComponent(doTarget, true);
          doTarget.add(doPrevRoot);
          doPrevRoot.position.set(0, 0, 0);
          doPrevRoot.quaternion.set(0, 0, 0, 1);
          doPrevRoot.scale.set(1, 1, 1);

          const editable = self.selectedObjects.find(o => o.asActor?.uuid === actorAccessor.actorId);
          editable?.rebuildHierarchy();
          self.updateOutliner();
          this.updateInspector();
        },
        () => {
          const undoActor = actorAccessor.resolveActor();
          if (!undoActor) return;
          const undoTarget = targetAccessor.resolveComponent();
          if (!undoTarget) return;
          const undoPrevRoot = prevRootAccessor.resolveComponent();
          if (!undoPrevRoot) return;

          undoPrevRoot.removeFromParent();
          undoActor.setRootComponent(undoPrevRoot, true);
          undoPrevRoot.position.copy(originalRootTransform.position);
          undoPrevRoot.quaternion.copy(originalRootTransform.quaternion);
          undoPrevRoot.scale.copy(originalRootTransform.scale);

          if (originalParentAccessor) {
            const undoParent = originalParentAccessor.resolveComponent();
            if (undoParent) {
              if (originalIndex >= 0 && originalIndex < undoParent.children.length) {
                undoParent.children.splice(originalIndex, 0, undoTarget);
                undoTarget.parent = undoParent;
              } else {
                undoParent.add(undoTarget);
              }
            }
          }

          const editable = self.selectedObjects.find(o => o.asActor?.uuid === actorAccessor.actorId);
          editable?.rebuildHierarchy();
          self.updateOutliner();
          this.updateInspector();
        },
      );
    },
    setObjectParent(targetComponent: ENGINE.SceneComponent, newParent: ENGINE.SceneComponent, index?: number) {
      const world = self.world;
      if (!world) return;

      if (!targetComponent || !(targetComponent instanceof ENGINE.SceneComponent)) {
        toast.warning('Target is not a scene component');
        return;
      }

      if (!newParent || !(newParent instanceof ENGINE.SceneComponent)) {
        toast.warning('New parent is not a scene component');
        return;
      }

      const targetActor = targetComponent.getActor();
      if (!targetActor) {
        toast.warning('No actor found for target component');
        return;
      }

      const parentActor = newParent.getActor();
      if (!parentActor) {
        toast.warning('No actor found for parent component');
        return;
      }

      if (targetActor !== parentActor) {
        toast.warning('Target and parent components must belong to the same actor');
        return;
      }

      const currentParent = targetComponent.parent;
      if (currentParent === newParent) {
        // If we're just changing the order within the same parent
        if (index !== undefined) {
          const currentIndex = newParent.children.indexOf(targetComponent);
          if (currentIndex === index) {
            toast.info('Component is already at the specified index');
            return;
          }
        } else {
          toast.info('Component is already a child of the specified parent');
          return;
        }
      }

      let isCircular = false;
      targetComponent.traverse((child) => {
        if (child === newParent) {
          isCircular = true;
        }
      });

      if (isCircular) {
        toast.warning('Cannot set parent: would create circular dependency');
        return;
      }

      const rootComponent = targetActor.rootComponent;
      if (targetComponent === rootComponent) {
        toast.warning('Root component cannot be reparented. Use makeObjectRoot to change the root component.');
        return;
      }

      const undoRedo = getSceneUndoRedo();

      // Build durable accessors outside the closures — UniversalAccessor stores IDs,
      // not live references, so it is safe to reuse across do/undo invocations.
      const actorAccessor = UniversalAccessor.forActor(targetActor.uuid);
      const targetAccessor = UniversalAccessor.forComponent(targetActor.uuid, targetComponent.uuid);
      const newParentAccessor = UniversalAccessor.forComponent(targetActor.uuid, newParent.uuid);
      const originalParentAccessor = currentParent
        ? UniversalAccessor.forComponent(targetActor.uuid, currentParent.uuid)
        : null;
      const originalIndex = currentParent ? currentParent.children.indexOf(targetComponent) : -1;

      void undoRedo.recordAction(
        'Set Object Parent',
        () => {
          const doTarget = targetAccessor.resolveComponent();
          if (!doTarget) return;
          const doNewParent = newParentAccessor.resolveComponent();
          if (!doNewParent) return;

          doTarget.removeFromParent();

          if (index !== undefined && index >= 0 && index < doNewParent.children.length) {
            doNewParent.children.splice(index, 0, doTarget);
            doTarget.parent = doNewParent;
          } else {
            doNewParent.add(doTarget);
          }

          const editable = self.selectedObjects.find(o => o.asActor?.uuid === actorAccessor.actorId);
          editable?.rebuildHierarchy();
          self.updateOutliner();
          this.updateInspector();
        },
        () => {
          const undoTarget = targetAccessor.resolveComponent();
          if (!undoTarget) return;

          undoTarget.removeFromParent();

          if (originalParentAccessor) {
            const undoOriginalParent = originalParentAccessor.resolveComponent();
            if (undoOriginalParent) {
              if (originalIndex >= 0 && originalIndex < undoOriginalParent.children.length) {
                undoOriginalParent.children.splice(originalIndex, 0, undoTarget);
                undoTarget.parent = undoOriginalParent;
              } else {
                undoOriginalParent.add(undoTarget);
              }
            }
          }

          const editable = self.selectedObjects.find(o => o.asActor?.uuid === actorAccessor.actorId);
          editable?.rebuildHierarchy();
          self.updateOutliner();
          this.updateInspector();
        },
      );
    },
    removeSelectedObjects() {
      this.removeObjects(...self.selectedObjects.map(obj => obj.asActor));
    },
    removeSelectedObjectsOrSubobjects() {
      if (self.selectedObjects.length > 1) {
        this.removeSelectedObjects();
      } else if ((self.selectedObject?.selectedSubobjects?.length ?? 0) > 1) {
        self.selectedObject?.selectedSubobjects.forEach(subobject => subobject.removeComponent());
      } else if (self.selectedObject?.selectedSubobject?.isRemovable) {
        self.selectedObject.selectedSubobject.removeComponent();
      } else if (self.selectedObject?.selectedSubobject?.asActor) {
        this.removeSelectedObjects(); // Remove the actor
      } else if (self.selectedObject?.selectedSubobject) {
        if (self.selectedObject.selectedSubobject.isRootComponent) {
          toast.info('Root Component is not removable.');
        } else if (self.selectedObject.selectedSubobject.isPrefabInstance) {
          toast.info('Component of Prefab Instance is not removable.');
        } else {
          toast.info('Component is not removable.');
        }
      }
    },
    addObject(target: string | ENGINE.Actor | ENGINE.SceneComponent, transform?: { position?: THREE.Vector3, rotation?: THREE.Euler, scale?: THREE.Vector3 }, skipAddingActorToWorld: boolean = false, selectAfterAdd: boolean = true): ENGINE.Actor | null {
      const intent = captureDefaultOutlinerPlacementIntent();
      const parentNodeId = intent.kind === 'folder' ? intent.parentNodeId : undefined;
      const sourceNodeId = intent.kind === 'afterActor' ? intent.sourceNodeId : undefined;

      return addObjectToWorld(
        self.world,
        target,
        skipAddingActorToWorld,
        () => self.updateOutliner(),
        selectAfterAdd,
        transform,
        sourceNodeId,
        parentNodeId
      );
    },
    duplicateSelectedObjectsOrSubobjects() {
      const world = self.world;
      if (!world) return;

      // selectedObjects that is not read only
      const selectedObjects = self.selectedObjects.filter(obj => !obj.isReadOnly || obj.isPrefabInstance);
      const selectedActors = selectedObjects.map(obj => obj.asActor).filter((actor): actor is ENGINE.Actor => actor?.getWorld() === world);

      // Only select components if there is only one selected actor.
      let selectedComponentNodes: IObjectTreeNode[] = [];
      let objectTree: IObjectTree | null = null;
      if (selectedObjects.length === 1) {
        objectTree = selectedObjects[0].hierarchy;
        selectedComponentNodes = objectTree.selectedNodes.filter(node => node.asComponent && !node.isTransient);
      }

      if (objectTree && selectedComponentNodes.length > 0) {
        if (DEBUG.logDuplicateObjects) {
          console.log(
            '[SceneStore] duplicateObjects - components',
            selectedComponentNodes.map(n => ({ id: n.id, name: n.name }))
          );
        }

        selectedComponentNodes.forEach(node => {
          const component = node.asComponent!;
          const attachTo = component.isRootComponent() ? component : component.parent;
          const parentNode = objectTree.getNodeById(attachTo?.uuid ?? '');
          if (parentNode) {
            parentNode.duplicateComponent(component, true);
          }
        });

      } else if (selectedActors.length > 0) {
        if (DEBUG.logDuplicateObjects) {
          console.log(
            '[SceneStore] duplicateObjects - actors',
            selectedActors.map(a => ({ uuid: a.uuid, name: a.name }))
          );
        }

        const selectionGroup = self.selectionGroup;
        const scene = self.scene;
        if (selectionGroup && scene) {
          objectHelpers.detachGroupChildren(selectionGroup, scene);
        }

        const existingNames = nameHelpers.collectOutlinerNames(editorStore.scene.outliner.rootNode ?? undefined);

        // Serialize each source actor upfront with fresh UUIDs and a unique display name.
        // Keeping the JSON + transform stable ensures redo recreates exactly the same actors.
        type PreparedDuplicate = { uuid: string; actorJson: string; worldTransform: ReturnType<ENGINE.Actor['getWorldTransform']> };
        const preparedDuplicates: PreparedDuplicate[] = [];

        for (const actor of selectedActors) {
          const sourceData = ENGINE.WorldSerializer.exportObject(actor);
          const duplicatedData = ENGINE.WorldSerializer.cloneWithNewUUIDs(sourceData);
          const duplicatedActor = ENGINE.WorldSerializer.loadActorSync(duplicatedData, editorAssets.prefabDataCopy, true);
          if (!duplicatedActor) continue;

          const currentName = nameHelpers.getActorDisplayName(duplicatedActor);
          const uniqueActorName = nameHelpers.makeUniqueName(currentName, existingNames, { generatedSeparator: ' ' });
          existingNames.push(uniqueActorName);
          nameHelpers.setCustomDisplayName(duplicatedActor, uniqueActorName);

          preparedDuplicates.push({
            uuid: duplicatedActor.uuid,
            actorJson: JSON.stringify(ENGINE.WorldSerializer.exportObject(duplicatedActor)),
            worldTransform: actor.getWorldTransform(),
          });
        }

        if (preparedDuplicates.length === 0) {
          self.updateSelectionGroup();
        } else {
          const lastSelectedActorUuid = selectedActors[selectedActors.length - 1].uuid;
          const duplicateUuids = preparedDuplicates.map(d => d.uuid);
          const actionName = preparedDuplicates.length === 1 ? 'Duplicate Actor' : 'Duplicate Actors';
          const undoRedo = getSceneUndoRedo();

          void undoRedo.recordAction(
            actionName,
            async () => {
              const liveWorld = editorStore.scene.world;
              if (!liveWorld) return;
              const addedActors: ENGINE.Actor[] = [];
              for (const { uuid, actorJson, worldTransform } of preparedDuplicates) {
                if (liveWorld.getActorByUuid(uuid)) continue;
                const actorToAdd = await loadActorAsync(actorJson);
                if (!actorToAdd) continue;
                actorToAdd.setWorldTransform(worldTransform);
                liveWorld.addActor(actorToAdd);
                addedActors.push(actorToAdd);
              }

              if (addedActors.length === 0) return;

              self.updateOutliner();

              const lastSourceNode = self.outliner.getNodeById(lastSelectedActorUuid);
              if (lastSourceNode?.isActorNode) {
                self.outliner.moveBehindNode(addedActors.map(a => a.uuid), lastSelectedActorUuid);
              }

              editorStore.scene.selectObjects(addedActors);
              self.outliner.scrollToSelected();
            },
            () => {
              const liveWorld = editorStore.scene.world;
              if (!liveWorld) return;
              const actorsToRemove = duplicateUuids
                .map(uuid => liveWorld.getActorByUuid(uuid))
                .filter((actor): actor is ENGINE.Actor => actor !== null && actor !== undefined);

              if (actorsToRemove.length === 0) return;

              editorStore.scene.selectObject(null);
              self.outliner.removeNodes(actorsToRemove.map(a => a.uuid));
              liveWorld.removeActors(...actorsToRemove);
              self.updateOutliner();
            },
          );
        }
      }
    },
    copySelectedActorsToClipboard: flow(function* copySelectedActorsToClipboardGen() {
      const world = self.world;
      if (!world) {
        toast.warning('No scene is loaded.');
        return false;
      }

      const selectedActors = self.outliner.selectedNodes
        .map(node => node.asActor)
        .filter((actor): actor is ENGINE.Actor => actor?.getWorld() === world);

      if (selectedActors.length === 0) {
        toast.info('Select at least one actor to copy.');
        return false;
      }

      const actors = selectedActors
        .map(actor => exportActor(actor))
        .filter((actor): actor is ENGINE.SerializedData => actor !== null);

      if (actors.length === 0) {
        toast.warning('Failed to serialise selected actors.');
        return false;
      }

      const payload: ActorClipboardPayload = {
        type: ACTOR_CLIPBOARD_PAYLOAD_TYPE,
        version: ACTOR_CLIPBOARD_PAYLOAD_VERSION,
        copiedAt: new Date().toISOString(),
        actors,
      };

      const success: boolean = yield writeClipboardText(JSON.stringify(payload));
      if (!success) {
        toast.error('Failed to write actor data to clipboard.');
        return false;
      }

      const label = actors.length === 1 ? 'Actor copied to clipboard.' : `${actors.length} actors copied to clipboard.`;
      toast.success(label);
      return true;
    }),
    pasteActorsFromClipboard: flow(function* pasteActorsFromClipboardGen(targetNodeId?: string): Generator<any, boolean, any> {
      const world = self.world;
      if (!world) {
        toast.warning('No scene is loaded.');
        return false;
      }

      const clipboardText: string | null = yield readClipboardText();
      if (!clipboardText) {
        toast.warning('Clipboard is empty or unavailable.');
        return false;
      }

      const payload = parseActorClipboardPayload(clipboardText);
      if (!payload) {
        toast.warning(`Clipboard does not contain ${PRODUCT_NAME} actor data.`);
        return false;
      }

      const placement = resolveActorPastePlacement(self.outliner, targetNodeId);
      const existingNames = nameHelpers.collectOutlinerNames(editorStore.scene.outliner.rootNode ?? undefined);

      const preparedActors: { uuid: string; actorJson: string }[] = [];
      for (const actorData of payload.actors) {
        const duplicatedData = ENGINE.WorldSerializer.cloneWithNewUUIDs(actorData);

        const duplicatedActor: ENGINE.Actor | undefined = yield loadActorAsync(duplicatedData);
        if (!duplicatedActor) {
          continue;
        }

        const duplicatedName = nameHelpers.getActorDisplayName(duplicatedActor);
        const uniqueName = nameHelpers.makeUniqueName(duplicatedName, existingNames, { generatedSeparator: ' ' });
        existingNames.push(uniqueName);
        nameHelpers.setCustomDisplayName(duplicatedActor, uniqueName);

        const actorSnapshot = ENGINE.WorldSerializer.exportObject(duplicatedActor);
        preparedActors.push({
          uuid: duplicatedActor.uuid,
          actorJson: JSON.stringify(actorSnapshot),
        });
      }

      if (preparedActors.length === 0) {
        toast.warning('No actors could be pasted from clipboard data.');
        return false;
      }

      const preparedActorUuids = preparedActors.map(actor => actor.uuid);
      const preparedActorJsons = [...preparedActors.map(actor => actor.actorJson)];
      const actionName = preparedActors.length === 1 ? 'Paste Actor' : 'Paste Actors';
      const undoRedo = getSceneUndoRedo();

      yield undoRedo.recordAction(
        actionName,
        async () => {
          const liveWorld = editorStore.scene.world;
          if (!liveWorld) return;
          const pastedActors: ENGINE.Actor[] = [];

          for (const actorJson of preparedActorJsons) {
            const actorToPaste = await loadActorAsync(actorJson);
            if (!actorToPaste) continue;
            if (liveWorld.getActorByUuid(actorToPaste.uuid)) continue;

            liveWorld.addActor(actorToPaste);
            pastedActors.push(actorToPaste);
          }

          if (pastedActors.length === 0) return;

          self.updateOutliner();

          if (placement.type === 'afterActor') {
            const targetNode = self.outliner.getNodeById(placement.targetActorId);
            if (targetNode?.isActorNode) {
              self.outliner.moveBehindNode(pastedActors.map(actor => actor.uuid), placement.targetActorId);
            }
          }

          editorStore.scene.selectObjects(pastedActors);
          self.outliner.scrollToSelected();
        },
        () => {
          const liveWorld = editorStore.scene.world;
          if (!liveWorld) return;
          const actorsToRemove = preparedActorUuids
            .map(uuid => liveWorld.getActorByUuid(uuid))
            .filter((actor): actor is ENGINE.Actor => actor !== null && actor !== undefined);

          if (actorsToRemove.length === 0) return;

          editorStore.scene.selectObject(null);
          self.outliner.removeNodes(actorsToRemove.map(actor => actor.uuid));
          liveWorld.removeActors(...actorsToRemove);
          self.updateOutliner();
        },
      );
      return true;
    }),
    dropSelectedActorsToSurface() {
      if (self.isTranformingObjects) return;

      const world = self.world;
      const scene = self.scene;
      if (!world || !scene) return;

      const selectedActors = self.selectedActors.filter(actor =>
        actor instanceof ENGINE.Actor &&
        actor.getWorld() === world &&
        actor.rootComponent != null
      );
      if (selectedActors.length === 0) return;

      const ignoreRoots = new Set<THREE.Object3D>();
      selectedActors.forEach(actor => {
        if (actor.rootComponent) ignoreRoots.add(actor.rootComponent);
      });

      const raycastTargets = collectDropRaycastTargets(scene, ignoreRoots, self.selectionGroup, self.hiddenGroup);
      const raycaster = new THREE.Raycaster();
      raycaster.near = DROP_TO_SURFACE_RAY_NEAR;
      raycaster.far = DROP_TO_SURFACE_RAY_FAR;

      const initialTransforms: DropTransformSnapshot[] = [];
      const finalTransforms: DropTransformSnapshot[] = [];

      for (const actor of selectedActors) {
        try {
          const initialTransform = captureActorWorldTransform(actor);
          if (!initialTransform) continue;

          const currentPosition = initialTransform.position;
          const bottomOffset = getActorBottomOffset(actor, currentPosition);

          const rayOrigin = currentPosition.clone();
          rayOrigin.y += Math.max(bottomOffset + DROP_TO_SURFACE_RAY_START_PADDING, DROP_TO_SURFACE_RAY_START_PADDING);

          raycaster.set(rayOrigin, DROP_TO_SURFACE_DOWN);
          const intersections = raycastTargets.length > 0 ? raycaster.intersectObjects(raycastTargets, false) : [];
          const targetSurfaceY = intersections[0]?.point?.y ?? DROP_TO_SURFACE_FALLBACK_Y;

          if (!Number.isFinite(targetSurfaceY)) continue;

          const nextPosition = currentPosition.clone();
          nextPosition.y = targetSurfaceY + bottomOffset;

          if (!Number.isFinite(nextPosition.y)) continue;

          if (Math.abs(nextPosition.y - currentPosition.y) <= DROP_TO_SURFACE_EPSILON) continue;

          initialTransforms.push(initialTransform);
          finalTransforms.push({
            actorUuid: actor.uuid,
            position: nextPosition,
            rotation: initialTransform.rotation.clone(),
            scale: initialTransform.scale.clone(),
          });
        } catch (error) {
          console.warn('[SceneStore] Failed to drop actor to surface:', error);
        }
      }

      if (finalTransforms.length === 0) return;

      const undoRedo = getSceneUndoRedo();
      void undoRedo.recordAction(
        'Drop To Surface',
        () => { applyDropTransforms(finalTransforms); },
        () => { applyDropTransforms(initialTransforms); },
      );
    },
    startTransformingObjects() { self.isTranformingObjects = true; },
    endTransformingObjects() { self.isTranformingObjects = false; },
    setTransformControls(transformControls: TransformControlsRef | null) {
      self.transformControls = transformControls;
    }
  }));

export interface ISceneStore extends Instance<typeof SceneStore> { }

// --- Module-local helpers (hoisted `function` so they are callable from the SceneStore definition above)

const DROP_TO_SURFACE_DOWN = new THREE.Vector3(0, -1, 0);
const DROP_TO_SURFACE_RAY_NEAR = 0;
const DROP_TO_SURFACE_RAY_FAR = 100_000;
const DROP_TO_SURFACE_RAY_START_PADDING = 0.1;
const DROP_TO_SURFACE_FALLBACK_Y = 0;
const DROP_TO_SURFACE_EPSILON = 0.0001;

interface DropTransformSnapshot {
  actorUuid: string;
  position: THREE.Vector3;
  rotation: THREE.Euler;
  scale: THREE.Vector3;
}

type ActorPastePlacement =
  | { type: 'afterActor'; targetActorId: string }
  | { type: 'root' };

function captureActorWorldTransform(actor: ENGINE.Actor): DropTransformSnapshot | null {
  const rootComponent = actor.rootComponent;
  if (!rootComponent) return null;

  const worldPosition = rootComponent.getWorldPosition();
  const worldQuaternion = rootComponent.getWorldQuaternion();
  const worldRotation = new THREE.Euler().setFromQuaternion(worldQuaternion, 'XYZ');
  const worldScale = rootComponent.getWorldScale();

  return {
    actorUuid: actor.uuid,
    position: worldPosition.clone(),
    rotation: worldRotation.clone(),
    scale: worldScale.clone(),
  };
}

function getActorBottomOffset(actor: ENGINE.Actor, currentPosition: THREE.Vector3): number {
  try {
    const bounds = actor.calcBoundingBox();
    if (bounds.isEmpty()) {
      return 0;
    }

    const bottomOffset = currentPosition.y - bounds.min.y;
    return Number.isFinite(bottomOffset) ? bottomOffset : 0;
  } catch (error) {
    console.warn('[SceneStore] Failed to calculate actor bounds for drop-to-surface:', error);
    return 0;
  }
}

function shouldSkipDropRaycastObject(
  object: THREE.Object3D,
  ignoreRoots: Set<THREE.Object3D>,
  selectionGroup: THREE.Group,
  hiddenGroup: THREE.Group,
): boolean {
  if (ignoreRoots.has(object)) return true;
  if (object === selectionGroup || object === hiddenGroup) return true;
  if (object instanceof THREE.Sprite) return true;
  if (object.userData?.isViewportGrid ?? false) return true;
  if (object.userData?.isSelectionHelper ?? false) return true;
  if (object.userData?.isTransformControls ?? false) return true;
  if (object.userData?.isTransformControlsGizmo ?? false) return true;
  if (object.userData?.isTransformControlsPlane ?? false) return true;
  return false;
}

function collectDropRaycastTargets(
  scene: THREE.Scene,
  ignoreRoots: Set<THREE.Object3D>,
  selectionGroup: THREE.Group,
  hiddenGroup: THREE.Group,
): THREE.Object3D[] {
  const targets: THREE.Object3D[] = [];

  const collect = (object: THREE.Object3D, ancestorIgnored: boolean = false) => {
    const ignored = ancestorIgnored || shouldSkipDropRaycastObject(object, ignoreRoots, selectionGroup, hiddenGroup);
    if (ignored) return;

    if (object instanceof THREE.Mesh && object.visible) {
      targets.push(object);
    }

    for (const child of object.children) {
      collect(child, false);
    }
  };

  for (const child of scene.children) {
    collect(child, false);
  }

  return targets;
}

function notifyActorDropTransformChanged(actor: ENGINE.Actor): void {
  const rootComponent = actor.rootComponent;
  if (!rootComponent) return;

  try {
    objectActions.notifyEditorPropertyChanged(actor, 'position', actor.getWorldPosition());
    objectActions.notifyEditorPropertyChanged(rootComponent, 'position', rootComponent.position);
  } catch (error) {
    console.warn('[SceneStore] Failed to notify actor transform change after drop:', error);
  }
}

function applyDropTransforms(snapshots: DropTransformSnapshot[]): void {
  const world = editorStore.scene.world;
  if (!world) return;

  for (const snapshot of snapshots) {
    try {
      const actor = world.getActorByUuid(snapshot.actorUuid);
      const rootComponent = actor?.rootComponent;
      if (!rootComponent || !actor) continue;

      actor.setWorldTransform({
        position: snapshot.position,
        rotation: snapshot.rotation,
        scale: snapshot.scale,
      });
      notifyActorDropTransformChanged(actor);
    } catch (error) {
      console.warn('[SceneStore] Failed to apply drop-to-surface transform snapshot:', error);
    }
  }
}

function isClipboardPayloadLike(value: unknown): value is Partial<ActorClipboardPayload> & Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function parseActorClipboardPayload(value: string): ActorClipboardPayload | null {
  try {
    const parsed: unknown = ENGINE.parseJson(value);
    if (!isClipboardPayloadLike(parsed)) {
      return null;
    }

    if (parsed.type !== ACTOR_CLIPBOARD_PAYLOAD_TYPE || parsed.version !== ACTOR_CLIPBOARD_PAYLOAD_VERSION) {
      return null;
    }

    if (!Array.isArray(parsed.actors) || parsed.actors.length === 0) {
      return null;
    }

    const validActors = parsed.actors.filter(actor => typeof actor === 'object' && actor !== null);
    if (validActors.length === 0) {
      return null;
    }

    return {
      type: ACTOR_CLIPBOARD_PAYLOAD_TYPE,
      version: ACTOR_CLIPBOARD_PAYLOAD_VERSION,
      copiedAt: typeof parsed.copiedAt === 'string' ? parsed.copiedAt : new Date().toISOString(),
      actors: validActors as ENGINE.SerializedData[],
    };
  } catch {
    return null;
  }
}

function resolveActorPastePlacement(outliner: { getNodeById: (id: string) => ISceneTreeNode | undefined; selectedNode?: ISceneTreeNode }, targetNodeId?: string): ActorPastePlacement {
  const selectedNode = targetNodeId ? outliner.getNodeById(targetNodeId) : outliner.selectedNode;
  if (selectedNode?.isActorNode) {
    return { type: 'afterActor', targetActorId: selectedNode.id };
  }
  return { type: 'root' };
}

async function writeClipboardText(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch (error) {
    console.warn('[SceneStore] Failed to write clipboard text:', error);
    return false;
  }
}

async function readClipboardText(): Promise<string | null> {
  try {
    const value = await navigator.clipboard.readText();
    return value || null;
  } catch (error) {
    console.warn('[SceneStore] Failed to read clipboard text:', error);
    return null;
  }
}

function waitForNextFrame(): Promise<void> {
  return new Promise(resolve => {
    requestAnimationFrame(() => resolve());
  });
}

async function waitForRendererDrain(renderer: ENGINE.IGenesysRenderer | null): Promise<void> {
  type WebGpuQueueDrain = { onSubmittedWorkDone?: () => Promise<void> };
  const gpuQueue = renderer?.asWebGPU()
    ? (get(renderer, 'backend.device.queue') as WebGpuQueueDrain | undefined)
    : undefined;

  if (gpuQueue?.onSubmittedWorkDone) {
    try {
      await gpuQueue.onSubmittedWorkDone();
      return;
    } catch (error) {
      console.warn('[SceneStore] Failed to wait for WebGPU queue drain, falling back to frame delay:', error);
    }
  }

  await waitForNextFrame();
  await waitForNextFrame();
}

/** Project-relative folder for MCP/editor-api viewport screenshot files. */
export const PROJECT_SCREENSHOTS_DIR = `${PROJECT_PATH_PREFIX}/assets/screenshots`;

export type CaptureScreenshotAsyncResult = {
  ok: boolean;
  scenePath: string | null;
  projectScreenshotKey: string | null;
  sceneScreenshotKey: string | null;
  projectScreenshotPath: string | null;
  sceneScreenshotPath: string | null;
  screenshotPath: string | null;
  requestedFilename: string | null;
  captureWidth: number | null;
  captureHeight: number | null;
  imageType: string;
  imageBase64: string | null;
  imageByteLength: number | null;
  warnings: string[];
};

function getScreenshotExtensionFromMimeType(imageType: string): string {
  const normalised = imageType.toLowerCase();
  if (normalised.includes('png')) {
    return 'png';
  }
  if (normalised.includes('webp')) {
    return 'webp';
  }
  return 'jpg';
}

function sanitizeScreenshotStem(name: string): string {
  const trimmed = name.trim().replace(/[^a-zA-Z0-9_-]+/g, '-').replace(/^-+|-+$/g, '');
  return trimmed.length > 0 ? trimmed : 'screenshot';
}

export function getSceneScreenshotStem(scenePath: string): string {
  const fileName = scenePath.split('/').pop() ?? 'scene';
  const stem = fileName.endsWith(ENGINE.SCENE_NAME_EXT)
    ? fileName.slice(0, -ENGINE.SCENE_NAME_EXT.length)
    : fileName.replace(/\.[^.]+$/, '');
  return sanitizeScreenshotStem(stem);
}

export function getProjectScreenshotAssetPath(imageType: string): string {
  const ext = getScreenshotExtensionFromMimeType(imageType);
  return `${PROJECT_SCREENSHOTS_DIR}/project.${ext}`;
}

export function getSceneScreenshotAssetPath(scenePath: string, imageType: string): string {
  const ext = getScreenshotExtensionFromMimeType(imageType);
  return `${PROJECT_SCREENSHOTS_DIR}/${getSceneScreenshotStem(scenePath)}.${ext}`;
}

function buildTimestampSuffix(): string {
  const now = new Date();
  const Y = now.getFullYear();
  const M = String(now.getMonth() + 1).padStart(2, '0');
  const D = String(now.getDate()).padStart(2, '0');
  const h = String(now.getHours()).padStart(2, '0');
  const m = String(now.getMinutes()).padStart(2, '0');
  const s = String(now.getSeconds()).padStart(2, '0');
  return `${Y}${M}${D}-${h}${m}${s}`;
}

function sanitizeScreenshotFilename(raw: string): string {
  // Strip path separators and any extension the caller may have included.
  const basename = raw.replace(/[\\/]/g, '').trim();
  const stem = basename.replace(/\.[^.]+$/, '');
  return sanitizeScreenshotStem(stem);
}

async function buildCollisionSafeExportPath(
  storageProvider: { exists(path: ENGINE.AssetPath): Promise<boolean> },
  stem: string,
  ext: string,
): Promise<string> {
  const base = `${PROJECT_SCREENSHOTS_DIR}/${stem}.${ext}`;
  const baseExists = await storageProvider.exists(ENGINE.AssetPath.fromString(base)).catch(() => false);
  if (!baseExists) {
    return base;
  }
  const ts = buildTimestampSuffix();
  const withTs = `${PROJECT_SCREENSHOTS_DIR}/${stem}-${ts}.${ext}`;
  const withTsExists = await storageProvider.exists(ENGINE.AssetPath.fromString(withTs)).catch(() => false);
  if (!withTsExists) {
    return withTs;
  }
  // Both base and timestamp variant exist — add a numeric suffix.
  for (let i = 2; i < 1000; i++) {
    const numbered = `${PROJECT_SCREENSHOTS_DIR}/${stem}-${ts}-${i}.${ext}`;
    const numberedExists = await storageProvider.exists(ENGINE.AssetPath.fromString(numbered)).catch(() => false);
    if (!numberedExists) {
      return numbered;
    }
  }
  // Extreme fallback: epoch millis avoids any collision.
  return `${PROJECT_SCREENSHOTS_DIR}/${stem}-${ts}-${Date.now()}.${ext}`;
}

async function blobToBase64(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return btoa(binary);
}

async function persistScreenshotToProjectAssets(
  blob: Blob,
  imageType: string,
  scenePath: string | null,
): Promise<{ projectScreenshotPath: string | null; sceneScreenshotPath: string | null; warnings: string[] }> {
  const warnings: string[] = [];
  let projectScreenshotPath: string | null = null;
  let sceneScreenshotPath: string | null = null;

  try {
    const storageProvider = editorStore.project.makeScopedStorageProvider();
    await storageProvider.createDirectory(ENGINE.AssetPath.fromString(PROJECT_SCREENSHOTS_DIR));

    const projectPath = getProjectScreenshotAssetPath(imageType);
    await storageProvider.uploadFile(
      ENGINE.AssetPath.fromString(projectPath),
      blob,
      { contentType: imageType },
    );
    projectScreenshotPath = projectPath;

    if (scenePath) {
      const sceneAssetPath = getSceneScreenshotAssetPath(scenePath, imageType);
      await storageProvider.uploadFile(
        ENGINE.AssetPath.fromString(sceneAssetPath),
        blob,
        { contentType: imageType },
      );
      sceneScreenshotPath = sceneAssetPath;
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    warnings.push(`Failed to save screenshot to project assets: ${message}`);
  }

  return { projectScreenshotPath, sceneScreenshotPath, warnings };
}

/** IndexedDB helpers for screenshot storage */
const DB_NAME = 'GenesysScreenshots';
const STORE_NAME = 'screenshots';
const DB_VERSION = 1;

export function openIndexedDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
  });
}

export function saveToIndexedDB(db: IDBDatabase, key: string, blob: Blob): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const request = store.put(blob, key);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

export function loadFromIndexedDB(db: IDBDatabase, key: string): Promise<Blob | null> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const request = store.get(key);
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
}

export function deleteFromIndexedDB(db: IDBDatabase, key: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const request = store.delete(key);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

export function getProjectScreenshotIdbKey(projectFolder: string | null | undefined): string | null {
  if (!projectFolder) {
    return null;
  }
  return projectFolder.replace(/\\/g, '/');
}

export function getProjectScopedSceneScreenshotIdbKey(
  projectFolder: string | null | undefined,
  scenePath: string | null | undefined
): string | null {
  if (!projectFolder) {
    return null;
  }
  const projectKey = getProjectScreenshotIdbKey(projectFolder);
  if (!projectKey) {
    return null;
  }
  const normalisedScenePath = normaliseSceneAssetPath(scenePath, projectFolder);
  if (!normalisedScenePath) {
    return null;
  }
  return `scene:${encodeURIComponent(projectKey)}:${encodeURIComponent(normalisedScenePath)}`;
}

function normaliseSceneAssetPath(scenePath: string | null | undefined, projectFolder: string): string | null {
  if (!scenePath) return null;

  const normalisedScenePath = scenePath.replace(/\\/g, '/');
  if (normalisedScenePath.startsWith(PROJECT_PATH_PREFIX + '/')) {
    return normalisedScenePath;
  }

  const normalisedProjectFolder = projectFolder.replace(/\\/g, '/');
  if (!normalisedProjectFolder) {
    return normalisedScenePath;
  }

  return normalisedScenePath.startsWith(normalisedProjectFolder + '/')
    ? `${PROJECT_PATH_PREFIX}${normalisedScenePath.slice(normalisedProjectFolder.length)}`
    : normalisedScenePath;
}

export function getLegacySceneScreenshotIdbKey(
  projectFolder: string,
  scenePath: string | null | undefined
): string | null {
  return normaliseSceneAssetPath(scenePath, projectFolder);
}

/** Removes dashboard + per-scene screenshot blobs from IndexedDB for the open project. */
export async function clearIndexedDbScreenshotsForCurrentProject(): Promise<void> {
  const projectFolder = editorStore.project.projectFolder;
  if (!projectFolder) {
    return;
  }
  const projectKey = getProjectScreenshotIdbKey(projectFolder);
  if (!projectKey) {
    return;
  }
  const db = await openIndexedDB();
  try {
    await deleteFromIndexedDB(db, projectKey);
    const sceneFiles = editorStore.assets.assetTree.sceneFiles;
    for (const file of sceneFiles) {
      const sceneScreenshotKey = getProjectScopedSceneScreenshotIdbKey(projectFolder, file.path);
      const legacyScenePath = getLegacySceneScreenshotIdbKey(projectFolder, file.path);
      if (sceneScreenshotKey) {
        await deleteFromIndexedDB(db, sceneScreenshotKey);
      }
      if (legacyScenePath) {
        await deleteFromIndexedDB(db, legacyScenePath);
      }
    }
  } finally {
    db.close();
  }
  for (const file of editorStore.assets.assetTree.sceneFiles) {
    file.refreshThumbnail();
  }
}

function safeAddActorsToWorld(world: ENGINE.World, context: string, ...actors: ENGINE.Actor[]): boolean {
  try {
    world.addActors(...actors);
    requestViewportRender(ViewportRenderReason.SceneStateChanged);
    return true;
  } catch (error) {
    reportEditorHookError({
      hook: 'onEditorAddToWorld',
      context,
      target: actors[0],
      error,
    });
    return false;
  }
}

function safeRemoveActorsFromWorld(world: ENGINE.World, context: string, ...actors: ENGINE.Actor[]): boolean {
  try {
    world.removeActors(...actors);
    requestViewportRender(ViewportRenderReason.SceneStateChanged);
    return true;
  } catch (error) {
    reportEditorHookError({
      hook: 'onEditorRemoveFromWorld',
      context,
      target: actors[0],
      error,
    });
    return false;
  }
}

/**
 * Outliner placement for new actors: same rules as addObject (folder → child; else after last selected actor).
 * Capture before mutating the world or selection (e.g. at drag-drop / placePrefabs start).
 */
export type OutlinerPlacementIntent =
  | { kind: 'folder'; parentNodeId: string }
  | { kind: 'afterActor'; sourceNodeId: string }
  | { kind: 'default' };

export function captureDefaultOutlinerPlacementIntent(): OutlinerPlacementIntent {
  const scene = editorStore.scene;
  const selectedOutlinerNode = scene.outliner.selectedNode;
  if (selectedOutlinerNode?.isFolder) {
    return { kind: 'folder', parentNodeId: selectedOutlinerNode.id };
  }
  const selectedActor = scene.selectedActors.length > 0 ? scene.selectedActors[scene.selectedActors.length - 1] : null;
  if (selectedActor) {
    return { kind: 'afterActor', sourceNodeId: selectedActor.uuid };
  }
  return { kind: 'default' };
}

export function applyDefaultOutlinerPlacementIntent(actorUuids: string[], intent: OutlinerPlacementIntent): void {
  if (actorUuids.length === 0) return;
  runInAction(() => {
    const scene = editorStore.scene;
    if (intent.kind === 'folder') {
      for (const id of actorUuids) {
        scene.outliner.addNode(id, intent.parentNodeId);
      }
    } else if (intent.kind === 'afterActor') {
      scene.outliner.moveBehindNode(actorUuids, intent.sourceNodeId);
    }
  });
}

function getValidatedSceneActorId(world: ENGINE.World | null, actorId: string | null | undefined): string | null {
  if (!world || !actorId) return null;
  const actor = world.getActorByUuid(actorId);
  if (!actor || actor.isTransient() || actor.getWorld() !== world) {
    return null;
  }
  return actor.uuid;
}

function addObjectToWorld(
  world: ENGINE.World | null,
  target: string | ENGINE.Actor | ENGINE.SceneComponent,
  skipAddingActorToWorld: boolean,
  updateOutliner: () => void,
  selectAfterAdd: boolean,
  transform: { position?: THREE.Vector3, rotation?: THREE.Euler, scale?: THREE.Vector3 } = {},
  sourceNodeId?: string,
  parentNodeId?: string,
): ENGINE.Actor | null {
  if (!world || !target) return null;

  if (typeof target === 'string') {
    const targetClass: string = target;
    const actorClassInfo: IClassInfo | undefined = editorAssets.actorClassMap.get(targetClass);
    const componentClassInfo: IClassInfo | undefined = editorAssets.componentClassMap.get(targetClass);
    target = actorClassInfo?.createInstance() ?? componentClassInfo?.createInstance();
    if (!target) {
      toast.warning(`Failed to add object: ${targetClass}`);
      return null;
    }
  }

  if (target instanceof ENGINE.SceneComponent) {
    const targetComponent = target as ENGINE.SceneComponent;
    target = ENGINE.Actor.create({ ...transform });
    target.setRootComponent(targetComponent, false);
  } else if (target instanceof ENGINE.Actor && (transform.position || transform.rotation || transform.scale)) {
    target.setWorldTransform(transform);
  }

  if (target instanceof ENGINE.Actor) {
    const undoRedo = getSceneUndoRedo();
    const actorUuid = target.uuid;

    // Initial action: We already spawned an actor, so we don't want to use the undo/redo system to spawn it again.
    // Since the actor is already present in the world, the redo operation will not perform any additional action.
    if (!skipAddingActorToWorld) { // only skip for the initial action
      if (!safeAddActorsToWorld(world, 'sceneStore.addObjectToWorld.initialAdd', target)) {
        toast.error(`Failed to add object: ${target.name}`);
        return null;
      }
    }

    updateOutliner();

    // Position the new actor: either inside a parent folder or behind a source node
    if (parentNodeId) {
      // Move the actor node inside the specified parent folder
      editorStore.scene.outliner.addNode(actorUuid, parentNodeId);
    } else if (sourceNodeId) {
      // Position the new actor behind the source node
      editorStore.scene.outliner.moveBehindNode(actorUuid, sourceNodeId);
    }

    const outlinerRoot = editorStore.scene.outliner.rootNode ?? undefined;
    const uniqueDisplayName = nameHelpers.assignUniqueActorDisplayName(target, outlinerRoot, { omitNodeId: actorUuid });
    editorStore.scene.outliner.getNodeById(actorUuid)?.setName(uniqueDisplayName);

    if (selectAfterAdd) {
      editorStore.scene.selectObject(target);
      requestAnimationFrame(() => {
        editorStore.scene.outliner.scrollToSelected();
      });
    }

    // Serialize the actor data for redo operations
    const actorData = ENGINE.parseJson(JSON.stringify(ENGINE.WorldSerializer.exportObject(target)));

    void undoRedo.recordAction(
      'Add Object',
      async () => {
        const liveWorld = editorStore.scene.world;
        if (!liveWorld) return;
        const existingActor = liveWorld.getActorByUuid(actorUuid);
        if (!existingActor) {
          const actorToAdd = await loadActorAsync(actorData);
          if (!actorToAdd) {
            toast.error('Failed to deserialize actor for redo.');
            return;
          }
          if (!safeAddActorsToWorld(liveWorld, 'sceneStore.addObjectToWorld.redoAdd', actorToAdd)) {
            toast.error(`Failed to re-add object: ${actorToAdd.name}`);
            return;
          }
          updateOutliner();

          if (parentNodeId) {
            editorStore.scene.outliner.addNode(actorUuid, parentNodeId);
          } else if (sourceNodeId) {
            editorStore.scene.outliner.moveBehindNode(actorUuid, sourceNodeId);
          }

          if (selectAfterAdd) {
            editorStore.scene.selectObject(actorToAdd);
            requestAnimationFrame(() => {
              editorStore.scene.outliner.scrollToSelected();
            });
          }
        }
      },
      () => {
        const liveWorld = editorStore.scene.world;
        if (!liveWorld) return;
        const actorToRemove = liveWorld.getActorByUuid(actorUuid);
        if (actorToRemove) {
          const sceneStore = editorStore.scene;
          sceneStore.selectObject(null);
          sceneStore.outliner.removeNodes([actorUuid]);
          if (safeRemoveActorsFromWorld(liveWorld, 'sceneStore.addObjectToWorld.undoRemove', actorToRemove)) {
            updateOutliner();
          }
        }
      },
    );

    return target;
  }

  return null;
}

function removeObjectsFromWorld(
  world: ENGINE.World | null,
  targets: (ENGINE.Actor | THREE.Object3D | null | undefined)[],
  outliner: {
    removeNodes: (ids: string[]) => void;
    rootNode?: unknown;
  },
  clearSelection: () => void,
  updateOutliner: () => void
): void {
  if (!world) return;

  const undoRedo = getSceneUndoRedo();

  const actors = compact(targets.map(target => objectHelpers.findActorFromTarget(target)));
  if (actors.length === 0) return;

  const actorIdsToRemove = actors.map(actor => actor.uuid);
  const fallbackActor = getFallbackActorAfterDelete(world, outliner.rootNode, actorIdsToRemove);

  if (fallbackActor) {
    editorStore.scene.selectObject(fallbackActor);
  } else {
    clearSelection(); // Clear all selections when no actor fallback exists
  }

  actors.forEach(actor => {
    actorParenting.handleActorRemoval(actor);
  });

  outliner.removeNodes(actorIdsToRemove);

  runInAction(() => {
    // Store all actor data as strings upfront for undo/redo support (avoid object ref issues)
    const actorJsons = actors.map(actor => {
      const otherActorData = ENGINE.WorldSerializer.exportObject(actor);
      return JSON.stringify(otherActorData);
    });
    const actorUuids = [...actorIdsToRemove];

    void undoRedo.recordAction(
      'Remove Objects',
      () => {
        const liveWorld = editorStore.scene.world;
        if (!liveWorld) return;
        const actorsToRemove = actorUuids
          .map(uuid => liveWorld.getActorsByPredicate((a: ENGINE.Actor) => a.uuid === uuid)[0])
          .filter(Boolean);
        if (actorsToRemove.length > 0) {
          actorsToRemove.forEach(actor => {
            actorParenting.handleActorRemoval(actor);
          });
          if (safeRemoveActorsFromWorld(liveWorld, 'sceneStore.removeObjectsFromWorld.redoRemove', ...actorsToRemove)) {
            updateOutliner();
          }
        }
      },
      async () => {
        const liveWorld = editorStore.scene.world;
        if (!liveWorld) return;
        const loadedActors = (await Promise.all(actorJsons.map(data => loadActorAsync(data))))
          .filter((actor): actor is ENGINE.Actor => Boolean(actor));
        if (loadedActors.length > 0) {
          if (safeAddActorsToWorld(liveWorld, 'sceneStore.removeObjectsFromWorld.undoAdd', ...loadedActors)) {
            updateOutliner();
            actorParenting.rebuildParentingFromTree(editorStore.scene.outliner.rootNode, liveWorld);
            if (loadedActors.length === 1) {
              editorStore.scene.selectObject(loadedActors[0]);
            }
          }
        }
      },
    );
  });
}

function getFallbackActorAfterDelete(
  world: ENGINE.World,
  rootNode: unknown,
  actorIdsToRemove: string[],
): ENGINE.Actor | null {
  const idsToRemove = new Set(actorIdsToRemove);
  const orderedActorIds = getActorIdsInTreeOrder(rootNode);
  if (orderedActorIds.length === 0) {
    return null;
  }

  let firstRemovedIndex = Number.POSITIVE_INFINITY;
  for (const removedId of actorIdsToRemove) {
    const index = orderedActorIds.indexOf(removedId);
    if (index !== -1 && index < firstRemovedIndex) {
      firstRemovedIndex = index;
    }
  }

  if (!Number.isFinite(firstRemovedIndex)) {
    return null;
  }

  for (let i = firstRemovedIndex - 1; i >= 0; i -= 1) {
    const candidateId = orderedActorIds[i];
    if (!candidateId || idsToRemove.has(candidateId)) {
      continue;
    }
    const candidateActor = world.getActorByUuid(candidateId);
    if (candidateActor && !candidateActor.isTransient()) {
      return candidateActor;
    }
  }

  for (let i = firstRemovedIndex + 1; i < orderedActorIds.length; i += 1) {
    const candidateId = orderedActorIds[i];
    if (!candidateId || idsToRemove.has(candidateId)) {
      continue;
    }
    const candidateActor = world.getActorByUuid(candidateId);
    if (candidateActor && !candidateActor.isTransient()) {
      return candidateActor;
    }
  }

  return null;
}

function getActorIdsInTreeOrder(
  rootNode: unknown,
): string[] {
  if (!isOutlinerTreeNodeLike(rootNode)) {
    return [];
  }

  const actorIds: string[] = [];
  const walk = (node: OutlinerTreeNodeLike) => {
    if (node.nodeType === 'actor') {
      actorIds.push(node.id);
    }
    for (const child of node.children) {
      walk(child);
    }
  };

  walk(rootNode);
  return actorIds;
}

interface OutlinerTreeNodeLike {
  id: string;
  nodeType: 'scene' | 'folder' | 'actor';
  children: OutlinerTreeNodeLike[];
}

function isOutlinerTreeNodeLike(node: unknown): node is OutlinerTreeNodeLike {
  if (!node || typeof node !== 'object') {
    return false;
  }
  const candidate = node as Partial<OutlinerTreeNodeLike>;
  return typeof candidate.id === 'string' &&
    (candidate.nodeType === 'scene' || candidate.nodeType === 'folder' || candidate.nodeType === 'actor') &&
    Array.isArray(candidate.children);
}
